import type { ExchangeTransactionDetail, ExchangeTransactionSummary } from './contracts';
import type { SupportThreadDetail, SupportMessage } from './contracts';
import type { PotTransferResult, WidgetPot, WidgetPotTransaction } from './contracts';
import {
  BeneficiaryCreateResult,
  CardCreateResult,
  CopCheckResult,
  OnboardBusinessRequest,
  OnboardBusinessResult,
  OnboardIndividualRequest,
  OnboardIndividualResult,
  WidgetApiOperations,
  WidgetBusinessMember,
  WidgetOnboardingKind,
  WidgetOnboardingRequirements,
  WidgetOnboardingCheckStatus,
  WidgetOnboardingStatus,
  WidgetOnboardingStepId,
  WidgetVerificationChannel,
  WidgetVerificationConfirmResult,
  WidgetVerificationSendResult,
  WidgetVerifyIdentityResult,
  ExchangeQuoteResult,
  InternationalPaymentPrecheckResult,
  InternationalPaymentResult,
  PaymentInitiationResult,
  InternalTransferResult,
  PaymentInitiationStatus,
  PaymentRequestCreateResult,
  derivePaymentInitiationStatus,
  invalidScheduledAtReason,
  SavingsAccountCloseResult,
  SavingsAccountCreateResult,
  SavingsInternalTransferResult,
  SavingsMaturitySetRequest,
  SavingsAccountRenameResult,
  SavingsMaturitySetResult,
  SavingsWithdrawResult,
  NominatedAccountConnectResult,
  NominatedAccountProcessResult,
  ProfileUpdateRequest,
  WidgetLookupOption,
  WidgetLookupType,
  WidgetNominatedAccount,
  WidgetNominatedAccountInstitution,
  WidgetProfile,
  WidgetAccount,
  WidgetAddressSuggestion,
  WidgetAuthFlowComplete,
  WidgetAuthFlowError,
  WidgetAuthFlowField,
  WidgetAuthFlowMethod,
  WidgetAuthFlowState,
  WidgetAuthFlowStep,
  WidgetBeneficiary,
  WidgetBeneficiaryRequirements,
  WidgetBulkPayment,
  WidgetBulkPaymentDetail,
  WidgetBulkPaymentTransfer,
  WidgetCard,
  WidgetCardLimit,
  WidgetCompanyDetails,
  WidgetCompanySummary,
  WidgetDataTopics,
  WidgetDocumentDownloadResult,
  WidgetNotification,
  WidgetPushRegisterResult,
  WidgetPushUnregisterResult,
  WidgetWalletCompleteProvisioningResult,
  WidgetWalletProvisioningPayload,
  WidgetStoredDocument,
  WidgetHandoffRecord,
  WidgetPage,
  WidgetPaymentRequest,
  WidgetPaymentScheme,
  WidgetSavingsAccount,
  WidgetSavingsAccountRequest,
  WidgetSavingsProduct,
  WidgetResolvedAddress,
  WidgetTransaction,
  WidgetScheduledPayment,
  WidgetTransactionDetail,
  WidgetTransferReason,
} from './contracts';
import {
  BUSINESS_SIGN_IN_UNSUPPORTED_MESSAGE,
  WidgetAuthIdentity,
  normaliseBusinessFollowUp,
  normaliseSubmitFollowUp,
  submitFollowUpOutcome,
  widgetAuthStatus,
} from './auth';
import { authFlowActionUnavailableMessage, authFlowNotStartedMessage } from './auth-flow';
import { createWidgetHost, HostError, WidgetHost, WidgetHostHandlers } from './host';
import { base64, createMockCollections, storageSchemaErrors } from './mock-collections';
import {
  OTP_CODE_LENGTH,
  OTP_CODE_REJECTED_MESSAGE,
  OTP_RATE_LIMIT,
  OTP_RESEND_AFTER_SECONDS,
  OnboardingVerificationStates,
  buildOnboardingFlow,
  isVerificationLive,
  normaliseOnboardingCheckStatus,
  onboardingRequiredFields,
  outstandingPrerequisites,
  prerequisiteUnmetMessage,
  verificationExpiresAt,
} from './onboarding';
import {
  CapabilityGrants,
  WidgetContext,
  DataDefinition,
  DataDocument,
  DataRecord,
  WidgetErrorCode,
  handoffGrant,
} from './protocol';
import { WIDGET_TOPIC_SUPPORT } from './support';
import type { WidgetEnrolledDevice } from './contracts/device';
import type { IdentityVerificationProfile, IdentityVerificationStartResult } from './contracts/identity';

/**
 * A host with fixture data and no side effects — used by the standalone dev
 * harness and by the app builder's canvas preview, where a real backend is
 * either absent or must not be touched. Every interaction is recorded so a
 * harness UI (or a test) can show what the widget attempted.
 */

export interface MockHostRecord {
  /**
   * 'auth' records a session change the widget asked for — a sign-in after a
   * submit, a login redirect, a logout. The mock does not navigate (nor does it
   * for `nav.goToPage`), so the record is how a harness or a test sees that it
   * would have.
   */
  kind: 'api' | 'nav' | 'notify' | 'confirm' | 'event' | 'form-set' | 'handoff' | 'auth' | 'function';
  detail: Record<string, unknown>;
}

/**
 * One seeded backend function: what this project's function answers offline.
 *
 * Return the output to resolve, or `{ error: { … } }` to reject with that code —
 * the two outcomes a real invoke has. Throwing a HostError from the fixture works
 * too, and is the shorter way to express a conditional failure.
 */
export type MockFunctionFixture = (
  payload: unknown,
) => unknown | { error: { code?: WidgetErrorCode; message?: string; details?: Record<string, unknown> } };

/**
 * What a seeded function answers: a fixture function, or a plain JSON value used
 * as the constant result of every invoke. The JSON form is what saved preview
 * scenarios hold — a scenario is stored JSON, so it can never carry a function.
 * A constant of shape `{ error: { … } }` rejects, same as a fixture returning it.
 */
export type MockFunctionSeed =
  MockFunctionFixture | Record<string, unknown> | unknown[] | string | number | boolean | null;

/**
 * A representative slice of what a real project pushes on the 'theme' stream —
 * the platform's default palette, resolved.
 *
 * It is deliberately not three tokens: a widget previewed against a near-empty
 * map renders on the `--fx-*` fallbacks and then looks *different* in production,
 * which is the exact failure a preview exists to catch. So it covers every
 * `--fx-*` derivation source plus a sample of each raw family the theme
 * generator emits (typography scale, radius steps, button and input vars), and
 * both vocabularies the generator publishes — the canonical `--color-*` names
 * and the legacy `--palette-color-*` / `--typography-color-*` / `--alerts-color-*`
 * aliases.
 *
 * Exported on its own so every offline surface (the SDK dev harness, the app
 * builder's gallery preview, the widget studio) shares one set of values.
 */
export const MOCK_WIDGET_THEME: Record<string, string> = {
  // Palette — canonical names.
  '--color-primary': '#3574fc',
  '--color-background': '#f6f6f6',
  '--color-surface': '#ffffff',
  '--color-border-color': '#ced4da',
  '--color-on-primary': '#ffffff',
  '--color-on-background': '#000b0c',
  '--color-on-background-muted': '#757575',
  '--color-on-surface-muted': '#757575',
  '--color-error': '#ef0606',
  '--color-success': '#1bb12f',
  '--color-warning': '#ffc008',
  '--color-hover': '#023197',
  '--font-family': 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  // Type scale (a sample; production emits all twelve steps).
  '--typography-h1-font-size': '72px',
  '--typography-h1-font-weight': '700',
  '--typography-h1-line-height': '1.25',
  '--typography-body1-font-size': '16px',
  '--typography-body1-font-weight': '400',
  '--typography-body1-line-height': '1.5',
  '--typography-labelText-font-size': '14px',
  '--typography-labelText-font-weight': '400',
  // Radius steps.
  '--radius-rad-1': '8px',
  '--radius-rad-1-top-left': '8px',
  '--radius-rad-1-top-right': '8px',
  '--radius-rad-1-bottom-right': '8px',
  '--radius-rad-1-bottom-left': '8px',
  '--radius-rad-2': '25px',
  '--radius-rad-2-top-left': '25px',
  '--radius-rad-2-top-right': '25px',
  '--radius-rad-2-bottom-right': '25px',
  '--radius-rad-2-bottom-left': '25px',
  '--space-xs': '4px',
  '--space-md': '16px',
  // Buttons — what .fx-btn-primary / .fx-btn-ghost inherit.
  '--primaryButtonIdleBackgroundColor': '#3574fc',
  '--primaryButtonIdleTextColor': '#ffffff',
  '--primaryButtonIdleBorderColor': '#3574fc',
  '--primaryButtonHoverBackgroundColor': '#023197',
  '--primaryButtonHoverTextColor': '#ffffff',
  '--primaryButtonHoverBorderColor': '#023197',
  '--primaryButtonBorderStyle': 'solid',
  '--primaryButtonBorderTopLeftRadius': '8px',
  '--secondaryButtonIdleBackgroundColor': '#ffffff',
  '--secondaryButtonIdleTextColor': '#3574fc',
  '--secondaryButtonIdleBorderColor': '#3574fc',
  '--secondaryButtonHoverBorderColor': '#023197',
  '--secondaryButtonHoverTextColor': '#023197',
  // Inputs — what .fx-input / .fx-field inherit.
  '--inputFieldIdleBackgroundColor': '#ffffff',
  '--inputFieldIdleTextColor': '#000b0c',
  '--inputFieldIdleBorderColor': '#ced4da',
  '--inputFieldActiveBackgroundColor': '#ffffff',
  '--inputFieldActiveTextColor': '#000b0c',
  '--inputFieldActiveBorderColor': '#3574fc',
  '--inputFieldLabelColor': '#757575',
  '--inputFieldBorderTopLeftRadius': '5px',
  '--inputFieldHeight': '55px',
};

/**
 * The renewal catalogue: what `savings.products.list { category: 'renewal' }`
 * answers, and the only product codes `savings.maturity.set` accepts. A
 * separate fixture rather than a flag on the list above, because the platform
 * serves them from a different provider endpoint. Deliberately NOT a member of
 * MOCK_TOPIC_FIXTURES: that map's keys are the well-known topic list, and a
 * renewal catalogue is an operation payload, not a topic.
 */
export const MOCK_SAVINGS_RENEWAL_PRODUCTS: WidgetSavingsProduct[] = [
  {
    productCode: 'FT-12M-R',
    productName: '12 Month Fixed Term Saver (Renewal)',
    accountType: 'fixed-term',
    currency: 'GBP',
    interestRate: 4.7,
    aer: 4.8,
    termMonths: 12,
    minBalance: 1000,
    description: 'Roll your maturing balance into another 12 months at a preferential rate.',
  },
  {
    productCode: 'FT-24M-R',
    productName: '24 Month Fixed Term Saver (Renewal)',
    accountType: 'fixed-term',
    currency: 'GBP',
    interestRate: 4.9,
    aer: 5.01,
    termMonths: 24,
    minBalance: 1000,
  },
];

/**
 * Savings applications that have not become accounts yet. One awaiting approval
 * (the state worth rendering) and one rejected, so both branches of a pending-
 * applications view exist offline. Not a topic, for the same reason the renewal
 * catalogue is not: MOCK_TOPIC_FIXTURES' keys are the well-known topic list.
 */
export const MOCK_SAVINGS_ACCOUNT_REQUESTS: WidgetSavingsAccountRequest[] = [
  {
    requestId: 'req-1',
    status: 'awaiting-approval',
    applicationNumber: '0000000042',
    productCode: 'FT-12M',
    accountType: 'fixed-term',
    accountName: 'Wedding fund',
    currency: 'GBP',
    termMonths: 12,
    interestRate: 4.5,
  },
  {
    requestId: 'req-2',
    status: 'rejected',
    applicationNumber: '0000000041',
    productCode: 'NT-90',
    accountType: 'notice',
    currency: 'GBP',
  },
];

export const MOCK_WIDGET_CONTEXT: WidgetContext = {
  widgetId: 'mock-widget',
  instanceId: 'mock-instance',
  settings: {},
  page: { zone: 'payments', url: 'mock-page', role: 'customer' },
  theme: MOCK_WIDGET_THEME,
  locale: 'en-GB',
  user: { id: 'mock-user', roles: ['customer'] },
};

export const MOCK_TOPIC_FIXTURES: WidgetDataTopics & Record<string, unknown> = {
  profile: {
    id: 'mock-user',
    firstName: 'Ada',
    middleName: 'Byron',
    lastName: 'Lovelace',
    email: 'ada@example.com',
    emailVerified: true,
    phone: '+447700900123',
    phoneVerified: true,
    dob: '1815-12-10',
    address: {
      country: 'GB',
      addressLine1: '12 Analytical Engine Way',
      city: 'London',
      county: 'Greater London',
      postalCode: 'EC1A 1BB',
    },
    marketingPreferences: { email: true, sms: false, push: true, mail: false },
    applicationReference: '0000001815',
    kyc: 'success',
  },
  accounts: [
    { accountId: 'acc-1', name: 'Main Account', currency: 'GBP', accountType: 'current', availableBalance: 1250.5 },
    { accountId: 'acc-2', name: 'Savings', currency: 'GBP', accountType: 'savings', availableBalance: 8400.0 },
  ],
  beneficiaries: [
    { beneficiaryId: 'ben-1', name: 'Grace Hopper', accountNumber: '12345678', sortCode: '04-00-04' },
    { beneficiaryId: 'ben-2', name: 'Alan Turing', accountNumber: '87654321', sortCode: '04-00-04' },
  ],
  transactions: [
    {
      transactionId: 'txn-1',
      amount: -42.0,
      currency: 'GBP',
      description: 'Coffee beans',
      date: '2026-07-28',
    },
    { transactionId: 'txn-2', amount: 1800.0, currency: 'GBP', description: 'Salary', date: '2026-07-25' },
  ],
  cards: [
    {
      cardId: 'card-1',
      name: 'Everyday debit',
      maskedPan: '**** **** **** 4242',
      status: 'active',
      type: 'physical',
      currency: 'GBP',
    },
    {
      cardId: 'card-2',
      name: 'Online shopping',
      maskedPan: '**** **** **** 8080',
      status: 'frozen',
      type: 'virtual',
      currency: 'GBP',
    },
    {
      cardId: 'card-3',
      name: 'Travel',
      maskedPan: '**** **** **** 3141',
      status: 'active',
      type: 'virtual',
      currency: 'EUR',
    },
  ],
  /**
   * The in-app feed `notifications.list` pages over. Reshaped onto WidgetNotification when
   * that contract landed (2026-08-29): the fixture used to carry `title`/`message`, which no
   * host has ever published — Novu's fields are `subject` and the body.
   */
  notifications: [
    {
      id: 'ntf-1',
      subject: 'Payment received',
      body: 'You received £1,800.00 from Analytical Engines Ltd',
      createdAt: '2026-07-28T09:15:00Z',
      read: false,
      kind: 'payment-received',
    },
    {
      id: 'ntf-2',
      subject: 'Beneficiary added',
      body: 'Grace Hopper was added to your beneficiaries',
      createdAt: '2026-07-25T14:02:00Z',
      read: true,
      kind: 'beneficiary-added',
    },
    {
      id: 'ntf-3',
      subject: 'Your statement is ready',
      body: 'Your July statement for 12 Month Fixed Term Saver is available',
      createdAt: '2026-08-01T06:00:00Z',
      read: false,
      kind: 'statement-ready',
    },
  ] as WidgetNotification[],
  /**
   * The project's alert copy, as the `alerts` topic serves it.
   *
   * The mock host's own refusals do not carry `alertCodes` — a `HostError` has no
   * field for them and the port transports never grew one, because alert copy is
   * an HTTP-envelope thing (contracts/alerts.ts). So this catalogue exists to be
   * resolved against by hand: `resolveAlerts({ alertCodes: ['insufficient_balance'] },
   * catalogue)` while a screen is being built, and the real overlay arrives once
   * it is pointed at a BFF.
   *
   * The codes are the mock's own failure vocabulary where it has one, plus three
   * that carry `{{placeholders}}` so interpolation is visible offline, plus one
   * `page` entry so a screen can see that a non-toaster alert is SKIPPED rather
   * than navigated to.
   */
  alerts: {
    locale: 'en-GB',
    alerts: {
      insufficient_balance: {
        code: 'insufficient_balance',
        kind: 'error',
        title: 'Not enough available balance',
        description: 'This account does not hold enough to cover the payment.',
        viewType: 'toaster',
      },
      withdrawal_disabled: {
        code: 'withdrawal_disabled',
        kind: 'error',
        title: 'Withdrawals are not available',
        description: 'This account’s terms do not allow a withdrawal right now.',
        viewType: 'toaster',
      },
      unknown_savings_account: {
        code: 'unknown_savings_account',
        kind: 'error',
        title: 'Account not found',
        description: 'We could not find that savings account.',
        viewType: 'toaster',
      },
      account_nickname_max_length: {
        code: 'account_nickname_max_length',
        kind: 'error',
        title: 'Name is too long',
        description: 'Account name cannot exceed {{maxLength}} characters.',
        viewType: 'toaster',
      },
      failed_to_create_beneficiary: {
        code: 'failed_to_create_beneficiary',
        kind: 'error',
        title: 'Recipient not added',
        description: 'We could not add {{beneficiaryName}}. Check the details and try again.',
        viewType: 'toaster',
      },
      payment_reference_too_long: {
        code: 'payment_reference_too_long',
        kind: 'error',
        title: 'Reference is too long',
        description: 'Description/Reference must not exceed {{characterLimit}} characters',
        viewType: 'toaster',
      },
      session_locked_out: {
        code: 'session_locked_out',
        kind: 'error',
        title: 'Account locked',
        description: 'Too many attempts. Try again later.',
        // Not a toaster on the platform's own surface, so `resolveAlerts` returns
        // nothing for it and the typed error stays the whole story.
        viewType: 'page',
      },
    },
  },
  /**
   * The documents the store already holds, keyed `${context}:${entityId}`, exactly as
   * `documents.list` addresses them. Two per savings account so a statements tab and a tax
   * tab over the same account are visibly different lists, plus one receipt hanging off a
   * transaction (which `documents.download` kind 'transactionReceipt' resolves) and one
   * customer document, so an upload/delete journey has something to start from.
   */
  storedDocuments: {
    'savings-account:sav-1': [
      {
        documentId: 'doc-sav1-stmt-2026-07',
        name: 'statement-2026-07.pdf',
        category: 'account-statements',
        contentType: 'application/pdf',
        createdAt: '2026-08-01T06:00:00Z',
      },
      {
        documentId: 'doc-sav1-tax-2025-26',
        name: 'tax-certificate-2025-26.pdf',
        category: 'tax-certificates',
        contentType: 'application/pdf',
        createdAt: '2026-04-08T06:00:00Z',
      },
    ],
    'savings-account:sav-2': [
      {
        documentId: 'doc-sav2-stmt-2026-07',
        name: 'statement-2026-07.pdf',
        category: 'account-statements',
        contentType: 'application/pdf',
        createdAt: '2026-08-01T06:00:00Z',
      },
    ],
    'transaction:txn-1': [
      {
        documentId: 'doc-txn1-receipt',
        name: 'receipt-txn-1.pdf',
        category: 'receipts',
        contentType: 'application/pdf',
        createdAt: '2026-07-28T09:16:00Z',
      },
    ],
    'customer:me': [
      {
        documentId: 'doc-me-passport',
        name: 'passport.jpg',
        category: 'customer-documents',
        contentType: 'image/jpeg',
        createdAt: '2026-02-11T11:20:00Z',
      },
    ],
  } as Record<string, WidgetStoredDocument[]>,
  savingsAccounts: [
    // Fixed-term pot after opening: the funding window has closed, so it can
    // no longer take money in — the flag pattern the savings providers
    // actually publish for this lifecycle state. A transfer INTO it, or a
    // deposit widget that gates on isFundingEnabled, exercises the disabled
    // path offline.
    {
      savingsAccountId: 'sav-1',
      productCode: 'FT-12M',
      productName: '12 Month Fixed Term Saver',
      accountName: 'House deposit',
      accountType: 'fixed-term',
      currency: 'GBP',
      country: 'GB',
      balance: 5000.0,
      availableBalance: 5000.0,
      interestRate: 4.5,
      aer: 4.59,
      termMonths: 12,
      startDate: '2026-01-15',
      endDate: '2027-01-15',
      status: 'active',
      accountNumber: '41234567',
      sortCode: '040004',
      // The fixed-term projections only a term product has, so a maturity
      // widget has something to render offline.
      interestAccrued: 132.19,
      interestAtMaturity: 225.0,
      balanceAtMaturity: 5225.0,
      deposits: 5000.0,
      maxDeposit: 85000,
      minBalance: 1000,
      lastFundingDate: '2026-01-15',
      maturityWindowOpensAt: '2026-12-16T00:00:00Z',
      isFundingEnabled: false,
      isWithdrawalEnabled: true,
      isClosureEnabled: false,
      isRenewalEnabled: false,
    },
    {
      savingsAccountId: 'sav-2',
      productCode: 'EA-01',
      productName: 'Easy Access Saver',
      accountType: 'easy-access',
      currency: 'GBP',
      country: 'GB',
      balance: 1350.75,
      availableBalance: 1350.75,
      interestRate: 3.2,
      aer: 3.25,
      status: 'active',
      accountNumber: '41234568',
      sortCode: '040004',
      interestAccrued: 11.4,
      deposits: 1500.0,
      minBalance: 1,
      lastFundingDate: '2026-07-02',
      // A limited-access pot: three withdrawals a year, one already taken. The
      // pot the withdrawal-allowance rendering path is exercised against
      // offline — sav-1 deliberately has no allowance block at all, which is
      // what an unlimited product looks like.
      withdrawalAllowance: {
        limit: 3,
        used: 1,
        remaining: 2,
        period: 'CALENDAR_YEAR',
        resetsAt: '2027-01-01T00:00:00Z',
      },
      isFundingEnabled: true,
      isWithdrawalEnabled: true,
      isClosureEnabled: true,
      isRenewalEnabled: false,
    },
  ],
  savingsProducts: [
    {
      productCode: 'FT-12M',
      productName: '12 Month Fixed Term Saver',
      accountType: 'fixed-term',
      currency: 'GBP',
      interestRate: 4.5,
      aer: 4.59,
      termMonths: 12,
      minBalance: 1000,
      maxDeposit: 85000,
      description: 'Lock your money away for 12 months at a fixed rate. No withdrawals until maturity.',
      documents: {
        keyProductInformationUrl: 'https://example.test/products/ft-12m/key-information.pdf',
        termsAndConditionsUrl: 'https://example.test/products/ft-12m/terms.pdf',
      },
    },
    {
      productCode: 'EA-01',
      productName: 'Easy Access Saver',
      accountType: 'easy-access',
      currency: 'GBP',
      interestRate: 3.2,
      aer: 3.25,
      minBalance: 1,
      description: 'Withdraw up to three times a year with no notice and no penalty.',
      // The product-level statement of sav-2's withdrawalAllowance.
      maxWithdrawalsPerYear: 3,
      documents: {
        keyProductInformationUrl: 'https://example.test/products/ea-01/key-information.pdf',
        termsAndConditionsUrl: 'https://example.test/products/ea-01/terms.pdf',
        interestRateTermsUrl: 'https://example.test/products/ea-01/rates.pdf',
      },
    },
    {
      productCode: 'NT-90',
      productName: '90 Day Notice Account',
      accountType: 'notice',
      currency: 'GBP',
      interestRate: 4.1,
      aer: 4.18,
      minBalance: 500,
      description: 'A higher rate in exchange for 90 days notice on every withdrawal.',
    },
  ],
  monthlySummary: { totalCredits: 2450.0, totalDebits: 1187.34, lastUpdated: '2026-07-29T22:00:00Z' },
  totalWealth: { results: { GBP: { currency: 'GBP', value: '9,650.50', fetching: false } } },
  spendingBudget: {
    isActive: true,
    limit: 1500,
    spent: 645.2,
    remaining: 854.8,
    resetDay: 1,
    periodStart: '2026-07-01',
    periodEnd: '2026-07-31',
  },
  depositAccountInfo: {
    currency: 'GBP',
    depositAmount: 5000,
    fee: 25,
    feeRate: 0.5,
    lastInterestCredit: { date: '2026-06-30', amount: 18.75, currency: 'GBP' },
    totalBalance: { currency: 'GBP', amount: 5000 },
    totalInterest: { currency: 'GBP', amount: 112.5, incrementPercentage: 2.25 },
    totalBalanceWithInterest: { currency: 'GBP', amount: 5112.5 },
  },
  pageContext: { selectedAccountId: 'acc-1', selectedCurrency: 'GBP' },
  // Hand-offs only exist once a widget starts one; the mock completes them
  // immediately (there is no vendor to redirect to) and appends them here.
  handoffs: [],
  // A fresh applicant: nothing submitted, nothing verified. The mock recomputes
  // `verified` and `canSubmit` from its own verification state on every read, so
  // seeding this topic seeds only kyc/kyb/profileComplete — which is the useful
  // half (render your success branch) and the half production does not derive
  // from the widget's own actions. See createMockWidgetHost.
  onboardingStatus: {
    kyc: 'not-started',
    kyb: null,
    profileComplete: false,
    verified: { email: { done: false }, phone: { done: false } },
    canSubmit: false,
  },
};

export const MOCK_TRANSFER_REASONS: WidgetTransferReason[] = [
  { id: 'reason-1', description: 'Family support' },
  { id: 'reason-2', description: 'Payment for goods or services' },
  { id: 'reason-3', description: 'Rent' },
];

/** transactions.detail responses echo the requested transactionId over this base. */
export const MOCK_TRANSACTION_DETAIL: WidgetTransactionDetail = {
  transactionId: 'txn-1',
  amount: -42.0,
  currency: 'GBP',
  date: '2026-07-28T08:41:00Z',
  postingDate: '2026-07-28T08:41:00Z',
  reference: 'Coffee beans',
  type: 'outgoing',
  status: 'completed',
  feeAmount: 0,
  totalAmount: -42.0,
  sender: { name: 'Ada Lovelace', accountNumber: '12345678', sortCode: '04-00-04' },
  receiver: { name: 'Analytical Roasters Ltd', accountNumber: '87654321', sortCode: '20-00-00' },
};

/**
 * The dates are computed from module load rather than fixed, because these
 * rows are inherently time-anchored: `expiryReversalAt` is a live countdown
 * target (a static date would render as already-fired the day after it was
 * written), and `scheduledAt` on sched-2 is the future-dated-inside-a-window
 * case — real via the initiation payload's `scheduledAt`.
 */
export const MOCK_SCHEDULED_PAYMENTS: WidgetPage<WidgetScheduledPayment> = {
  items: [
    {
      paymentId: 'sched-1',
      amount: 950.0,
      currency: 'GBP',
      beneficiaryName: 'Grace Hopper',
      reference: 'Rent August',
      sourceAccountId: 'acc-1',
      expiryReversalAt: new Date(Date.now() + 15 * 60_000).toISOString(),
    },
    {
      paymentId: 'sched-2',
      amount: 60.0,
      currency: 'GBP',
      beneficiaryName: 'Alan Turing',
      reference: 'Chess club',
      sourceAccountId: 'acc-1',
      expiryReversalAt: new Date(Date.now() + 45 * 60_000).toISOString(),
      scheduledAt: new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10),
    },
  ],
  page: 1,
  totalItems: 2,
};

export const MOCK_PAYMENT_REQUESTS: WidgetPage<WidgetPaymentRequest> = {
  items: [
    {
      paymentRequestId: 'preq-1',
      approvalRecordId: '00000000-0000-4000-8000-000000000001',
      paymentType: 'single',
      amount: 320.0,
      currency: 'GBP',
      reference: 'Team offsite',
      reason: 'Payment for goods or services',
      status: 'pending',
      createdDate: '2026-07-27T11:30:00Z',
      requesterName: 'Grace Hopper',
      beneficiaryName: 'Compilers R Us Ltd',
      accountId: 'acc-1',
      accountName: 'Main Account',
    },
    {
      paymentRequestId: 'preq-2',
      approvalRecordId: '00000000-0000-4000-8000-000000000002',
      paymentType: 'single',
      amount: 85.5,
      currency: 'GBP',
      reference: 'Stationery',
      reason: 'Payment for goods or services',
      status: 'approved',
      createdDate: '2026-07-20T09:05:00Z',
      approvedOrRejectedDate: '2026-07-21T10:12:00Z',
      requesterName: 'Alan Turing',
      approverName: 'Ada Lovelace',
      beneficiaryName: 'Paper Tape Supplies',
      accountId: 'acc-1',
      accountName: 'Main Account',
    },
  ],
  page: 1,
  totalItems: 2,
};

export const MOCK_CARD_LIMITS: WidgetCardLimit[] = [
  { limitType: 'DAILY', trxGroup: 'ATM', currency: 'GBP', limit: 300, spent: 50, available: 250 },
  {
    limitType: 'MONTHLY',
    trxGroup: 'TRANSACTION',
    currency: 'GBP',
    limit: 5000,
    spent: 1187.34,
    available: 3812.66,
  },
];

/** Fixed rate the mock quotes at, so a demo widget's arithmetic is predictable. */
export const MOCK_EXCHANGE_RATE = 1.17;

/** How long a mock quote is held — long enough for a countdown to be visible. */
export const MOCK_QUOTE_TTL_SECONDS = 90;

/**
 * Deterministic quote: the requested side is taken at face value and the other
 * is derived at MOCK_EXCHANGE_RATE, with a flat 1.50 fee in the sell currency.
 */
function mockExchangeQuote(payload: Record<string, unknown>, quoteId: string): ExchangeQuoteResult {
  const sellCurrency = String(payload['sellCurrency'] ?? 'GBP');
  const buyCurrency = String(payload['buyCurrency'] ?? 'EUR');
  const amount = Number(payload['amount'] ?? 0);
  const fixedSide = payload['fixedSide'] === 'buy' ? 'buy' : 'sell';
  const buyAmount = fixedSide === 'buy' ? amount : Number((amount * MOCK_EXCHANGE_RATE).toFixed(2));
  const sellAmount = fixedSide === 'buy' ? Number((amount / MOCK_EXCHANGE_RATE).toFixed(2)) : amount;
  const feeAmount = 1.5;
  return {
    quoteId,
    buyCurrency,
    buyAmount,
    sellCurrency,
    sellAmount,
    exchangeRate: MOCK_EXCHANGE_RATE,
    expiresAt: new Date(Date.now() + MOCK_QUOTE_TTL_SECONDS * 1000).toISOString(),
    cutOffTimeInMinutes: 2,
    feeAmount,
    feeCurrency: sellCurrency,
    fixedSide,
    exchangeDate: new Date().toISOString(),
    settlementDate: new Date().toISOString(),
    debitAmount: Number((sellAmount + feeAmount).toFixed(2)),
    creditAmount: buyAmount,
  };
}

/** Rails the mock offers; the real host answers per country/currency. */
export const MOCK_PAYMENT_SCHEMES: WidgetPaymentScheme[] = [
  { label: 'Faster Payments', value: 'faster-payments' },
  { label: 'CHAPS', value: 'chaps' },
  { label: 'SEPA Credit Transfer', value: 'sepa' },
  { label: 'SWIFT', value: 'swift' },
];

export const MOCK_COMPANIES: WidgetCompanySummary[] = [
  {
    companyName: 'Analytical Engines Limited',
    companyNumber: '01234567',
    companyStatus: 'active',
    type: 'ltd',
    dateOfCreation: '2015-06-12',
    registeredOfficeAddress: {
      address1: '12 Analytical Way',
      locality: 'London',
      postalCode: 'WC1A 1AA',
      country: 'United Kingdom',
    },
  },
  {
    companyName: 'Difference Engine Labs Limited',
    companyNumber: '07654321',
    companyStatus: 'active',
    type: 'ltd',
    dateOfCreation: '2020-03-04',
    registeredOfficeAddress: {
      address1: '4 Difference Street',
      locality: 'Manchester',
      postalCode: 'M1 1AE',
      country: 'United Kingdom',
    },
  },
];

export const MOCK_COMPANY_DETAILS: WidgetCompanyDetails[] = MOCK_COMPANIES.map((company) => ({
  ...company,
  jurisdiction: 'england-wales',
  officers: {
    activeCount: 1,
    resignedCount: 0,
    totalResults: 1,
    items: [
      {
        name: 'LOVELACE, Ada',
        firstName: 'Ada',
        lastName: 'Lovelace',
        officerRole: 'director',
        appointedOn: company.dateOfCreation,
        dateOfBirth: { month: 12, year: 1985 },
      },
    ],
  },
}));

export const MOCK_ADDRESS_SUGGESTIONS: WidgetAddressSuggestion[] = [
  { id: 'addr-1', label: '12 Analytical Way, London, WC1A 1AA' },
  { id: 'addr-2', label: '4 Difference Street, Manchester, M1 1AE' },
];

export const MOCK_RESOLVED_ADDRESSES: Record<string, WidgetResolvedAddress> = {
  'addr-1': {
    postcode: 'WC1A 1AA',
    latitude: '51.5200',
    longitude: '-0.1200',
    formattedAddress: ['12 Analytical Way', 'London', 'WC1A 1AA'],
    country: 'United Kingdom',
    city: 'London',
    street: 'Analytical Way',
    propertyNumber: '12',
    line1: '12 Analytical Way',
  },
  'addr-2': {
    postcode: 'M1 1AE',
    latitude: '53.4808',
    longitude: '-2.2426',
    formattedAddress: ['4 Difference Street', 'Manchester', 'M1 1AE'],
    country: 'United Kingdom',
    city: 'Manchester',
    street: 'Difference Street',
    propertyNumber: '4',
    line1: '4 Difference Street',
  },
};

/**
 * Flat 4.50 fee on international payments, so a precheck and the submission
 * that follows it agree and a demo widget's arithmetic is predictable.
 */
export const MOCK_INTERNATIONAL_PAYMENT_FEE = 4.5;

function mockInternationalPayment(
  payload: Record<string, unknown>,
  beneficiaries: WidgetBeneficiary[],
  accounts: WidgetAccount[],
): InternationalPaymentPrecheckResult {
  const amount = Number(payload['amount'] ?? 0);
  const beneficiary = beneficiaries.find((candidate) => candidate.beneficiaryId === payload['beneficiaryId']);
  const account = accounts.find((candidate) => candidate.accountId === payload['sourceAccountId']);
  const result: InternationalPaymentPrecheckResult = {
    amount,
    currency: beneficiary?.currency ?? account?.currency ?? 'GBP',
    feeAmount: MOCK_INTERNATIONAL_PAYMENT_FEE,
    totalAmount: Number((amount + MOCK_INTERNATIONAL_PAYMENT_FEE).toFixed(2)),
  };
  if (beneficiary?.name) result.receiverName = beneficiary.name;
  if (beneficiary?.accountNumber) result.receiverAccountNumber = beneficiary.accountNumber;
  if (account?.name) result.senderName = account.name;
  if (account?.accountNumber) result.senderAccountNumber = account.accountNumber;
  return result;
}

export const MOCK_BULK_PAYMENTS: WidgetPage<WidgetBulkPayment> = {
  items: [
    {
      bulkPaymentId: 'bulk-1',
      status: 'completed',
      fileName: 'july-payroll.csv',
      accountId: 'acc-1',
      accountName: 'Main Account',
      currency: 'GBP',
      totalAmount: 12450.75,
      totalPayments: 3,
      totalSuccessPayments: 2,
      totalFailedPayments: 1,
      totalDuplicatePayments: 0,
      totalUniqueBeneficiaries: 3,
      totalErrors: 0,
      createdDate: '2026-07-25T08:00:00Z',
      executedDate: '2026-07-25T08:05:00Z',
    },
    {
      bulkPaymentId: 'bulk-2',
      status: 'pending',
      fileName: 'supplier-run.csv',
      accountId: 'acc-1',
      accountName: 'Main Account',
      currency: 'GBP',
      totalAmount: 980,
      totalPayments: 2,
      totalSuccessPayments: 0,
      totalFailedPayments: 0,
      totalUniqueBeneficiaries: 2,
      createdDate: '2026-07-29T16:30:00Z',
    },
  ],
  page: 1,
  totalItems: 2,
};

/**
 * Rows of MOCK_BULK_PAYMENTS.items[0]: one settled, one failed so a widget can
 * exercise bulkPayments.retry. Keyed by batch so the mock can answer .get for
 * either id.
 */
export const MOCK_BULK_PAYMENT_TRANSFERS: Record<string, WidgetBulkPaymentTransfer[]> = {
  'bulk-1': [
    {
      transferId: 'bulk-1-row-1',
      beneficiaryName: 'Grace Hopper',
      beneficiaryId: 'ben-1',
      amount: 8200.5,
      currency: 'GBP',
      status: 'completed',
      transactionStatus: 'completed',
      transactionId: 'txn-90',
      reference: 'July salary',
      row: '1',
    },
    {
      transferId: 'bulk-1-row-2',
      beneficiaryName: 'Alan Turing',
      beneficiaryId: 'ben-2',
      amount: 4250.25,
      currency: 'GBP',
      status: 'failed',
      transactionStatus: 'failed',
      reference: 'July salary',
      row: '2',
      isError: true,
    },
  ],
  'bulk-2': [
    {
      transferId: 'bulk-2-row-1',
      beneficiaryName: 'Grace Hopper',
      beneficiaryId: 'ben-1',
      amount: 480,
      currency: 'GBP',
      status: 'pending',
      reference: 'Invoice 1042',
      row: '1',
    },
    {
      transferId: 'bulk-2-row-2',
      beneficiaryName: 'Alan Turing',
      beneficiaryId: 'ben-2',
      amount: 500,
      currency: 'GBP',
      status: 'pending',
      scheduleType: 'scheduled',
      scheduleDate: '2026-08-01',
      row: '2',
    },
  ],
};

/** UK faster-payments-flavoured requirements: sort code + account number. */
export const MOCK_BENEFICIARY_REQUIREMENTS: WidgetBeneficiaryRequirements = {
  bankAccount: {
    fields: {
      accountNumber: { requirement: 'required', regex: '^[0-9]{8}$' },
      iban: { requirement: 'none' },
      bankName: { requirement: 'optional' },
    },
    routingCodes: {
      'sort-code': { requirement: 'required', regex: '^[0-9]{6}$' },
    },
    addressRequirement: 'optional',
  },
  address: {
    country: { requirement: 'required' },
    city: { requirement: 'optional' },
    addressLine1: { requirement: 'optional' },
    postalCode: { requirement: 'optional' },
  },
};

/**
 * Deterministic CoP behaviour for demos/tests: exact fixture-name match ->
 * match; same name modulo case/spacing -> close_match with the actual name;
 * sort code 000000 (any punctuation stripped) -> not_supported; anything else -> no_match.
 */
/**
 * `records.*` takes a Data Studio slug; storage is keyed by `data/<slug>`, and the
 * reference form is accepted too — the same leniency the BFF has, so a
 * `records.list` entry can be fed straight back in.
 */
function recordReference(payload: Record<string, unknown>): string {
  const definition = String(payload['definition'] ?? '').trim();
  if (!definition) throw new HostError('host-error', 'records.* requires a definition slug');
  return definition.startsWith('data/') ? definition : `data/${definition}`;
}

function mockCopCheck(payload: Record<string, unknown>, beneficiaries: WidgetBeneficiary[]): CopCheckResult {
  const normalise = (value: unknown) =>
    String(value ?? '')
      .replace(/\s+/g, ' ')
      .trim();
  const requested = normalise(payload['name']);
  if (String(payload['sortCode'] ?? '').replace(/\D/g, '') === '000000') return { status: 'not_supported' };
  const exact = beneficiaries.find((candidate) => normalise(candidate.name) === requested);
  if (exact) return { status: 'match' };
  const close = beneficiaries.find(
    (candidate) => normalise(candidate.name).toLowerCase() === requested.toLowerCase(),
  );
  if (close) return { status: 'close_match', actualName: close.name };
  return { status: 'no_match', reasonCode: 'ANNM' };
}

/**
 * The codes the mock accepts, per channel — and they are the right *length*
 * (4 for email, 6 for phone), so a code-entry UI built against the mock has the
 * right number of boxes in production too.
 */
export const MOCK_OTP_CODES: Record<WidgetVerificationChannel, string> = {
  email: '1234',
  phone: '123456',
};

/* ── managed login flow (auth.flow.*) ───────────────────────────────────── */

/**
 * One scripted step of the mock's login flow.
 *
 * The mock is a *script*, not a Keycloak: it cannot decide which step comes next
 * from the customer's credentials, so the seed says. What it does reproduce
 * faithfully is the shape of a step, the error-on-step behaviour and the
 * host-side secrecy — no continuation URL exists here to leak, and completion
 * hands back a boolean rather than tokens, exactly as production does.
 */
export interface MockAuthFlowStepSeed {
  /** The step type a widget switches on (see AUTH_FLOW_KNOWN_STEP_TYPES). */
  type: string;
  fields?: WidgetAuthFlowField[];
  /**
   * The alternatives this step offers. Only these names are accepted; anything
   * else is a HostError, as the server answers `action_not_available`.
   *
   * 'restart_flow' returns to step 0. Every other listed name re-issues the
   * current step unchanged — which is exactly `resend_code`'s real behaviour
   * (invalidate the challenge, send another, same step back), and is the honest
   * answer for a recovery branch the mock has no script for.
   */
  actions?: string[];
  /** "Try another way" choices; `goTo` makes the choice lead somewhere. */
  methods?: MockAuthFlowMethodSeed[];
  metadata?: Record<string, unknown>;
  expiresIn?: number;
  /**
   * Values that must match exactly for the step to advance ('123456' for an OTP).
   * A field not named here accepts any non-empty value.
   */
  expect?: Record<string, string>;
  /** Values that are *rejected* — the canonical wrong password, for instance. */
  reject?: Record<string, string>;
  /** The error the step comes back with when `expect`/`reject` is not satisfied. */
  error?: WidgetAuthFlowError;
}

/** A seeded "try another way" choice. */
export interface MockAuthFlowMethodSeed extends WidgetAuthFlowMethod {
  /** Which script step this choice leads to. Defaults to the next one. */
  goTo?: number;
}

/**
 * The password the mock rejects. Every other non-empty value is accepted — the
 * mock validates no credentials, it only needs *a* wrong-password path so the
 * "your details were not recognised" screen is buildable offline.
 *
 * A contract with the test, not with the widget: nothing returns it, and a
 * widget cannot learn it from the mock any more than it can learn a real one.
 */
export const MOCK_AUTH_FLOW_WRONG_PASSWORD = 'wrong-password';

/** The SMS code the mock's default script accepts. Same caveat as above. */
export const MOCK_AUTH_FLOW_OTP_CODE = '123456';

/**
 * The default script: password, then an SMS code, then signed in. Two steps
 * because one is not enough to exercise the thing widgets get wrong — carrying
 * an error on a *re-issued* step rather than as a rejection, and doing it twice
 * in a row on different step types.
 *
 * Seed `authFlow: { steps: [...] }` for anything else (a TOTP enrolment, a
 * method selector, an unknown type to prove the fail-closed branch renders).
 */
export const MOCK_AUTH_FLOW_SCRIPT: MockAuthFlowStepSeed[] = [
  {
    type: 'username_password',
    fields: [
      { name: 'username', type: 'text', required: true, autocomplete: 'username', maxLength: 254 },
      { name: 'password', type: 'password', required: true, autocomplete: 'current-password', minLength: 1 },
    ],
    actions: ['forgot_password', 'restart_flow'],
    reject: { password: MOCK_AUTH_FLOW_WRONG_PASSWORD },
    // Deliberately unattributed: the real flow never says which of the two was
    // wrong, and a mock that did would teach a widget to render an enumeration
    // oracle.
    error: { code: 'invalid_credentials', message: 'Those details were not recognised' },
  },
  {
    type: 'sms_otp',
    fields: [
      {
        name: 'code',
        type: 'text',
        required: true,
        autocomplete: 'one-time-code',
        minLength: 6,
        maxLength: 6,
      },
    ],
    actions: ['resend_code', 'restart_flow'],
    metadata: { maskedDestination: '***0124' },
    expiresIn: 300,
    expect: { code: MOCK_AUTH_FLOW_OTP_CODE },
    error: { code: 'invalid_code', message: 'That code was not correct', field: 'code' },
  },
];

/**
 * The mock project's requirements. `extraFieldsKnown: true` with a non-empty
 * list is the interesting default: it exercises the branch where the widget must
 * render extra inputs.
 *
 * Test the *other* branch — a project that publishes nothing — by passing
 * `onboarding: { requirements: { …, extraFields: [], extraFieldsKnown: false } }`
 * to createMockWidgetHost. Both branches are real: plenty of projects configure
 * no mandatory-extras list at all.
 */
export const MOCK_ONBOARDING_REQUIREMENTS: WidgetOnboardingRequirements = {
  kind: 'individual',
  requiredFields: onboardingRequiredFields('individual'),
  extraFields: ['title', 'productCode', 'sourceOfFunds'],
  extraFieldsKnown: true,
};

/**
 * The session a mock individual submit hands back, standing in for the platform's
 * auto-login tokens.
 *
 * It is a *marker*, not a token: the mock never returns it, records it, or lets it
 * near the widget — exactly like the real host, whose access/refresh tokens stay
 * in the bridge. Turn it off with `onboarding: { autoLoginAvailable: false }` to
 * exercise the branch where the platform's best-effort token generation failed
 * and 'signIn' has to fall back to the login page. Both branches are real.
 */
export const MOCK_ONBOARDING_AUTO_LOGIN_AVAILABLE = true;

/**
 * The organisation and primary user a mock business submit creates. `redirected`
 * is decided per call by `onSuccess`, so it is not part of the fixture.
 */
export const MOCK_ONBOARDING_BUSINESS_RESULT: Omit<OnboardBusinessResult, 'redirected'> = {
  organisationId: 'org-mock-1',
  primaryUserId: 'user-mock-1',
  status: 'pending',
};

/**
 * Validates a business payload the way the *service* does rather than the way its
 * DTO is annotated — the DTO marks `members` optional but the service rejects an
 * empty list, and it trims several member fields unconditionally, so a missing
 * one is a 500 rather than a 400. The real bridge runs the same checks for the
 * same reason: a widget author should meet these as a clear rejection, not as a
 * server error.
 */
export function validateMockBusinessOnboarding(payload: Partial<OnboardBusinessRequest>): string | null {
  if (!payload.name) return 'onboarding.submitBusiness requires name';
  if (!payload.identificationNumber) return 'onboarding.submitBusiness requires identificationNumber';
  const address = payload.companyAddress;
  if (!address?.country || !address?.city || !address?.postalCode) {
    return 'onboarding.submitBusiness requires companyAddress country, city and postalCode';
  }
  const members = payload.members ?? [];
  if (!members.length) return 'onboarding.submitBusiness requires at least one member';
  const primary = members.filter((member) => member?.isPrimary);
  if (primary.length !== 1) {
    return 'onboarding.submitBusiness requires exactly one member with isPrimary: true';
  }
  for (const member of members) {
    if (
      !member?.position ||
      !member?.firstName ||
      !member?.lastName ||
      !member?.email ||
      !member?.phone ||
      !member?.dob
    ) {
      return 'onboarding.submitBusiness members require position, firstName, lastName, email, phone and dob';
    }
  }
  return null;
}

/** The single member that becomes the platform user; undefined if malformed. */
export function primaryBusinessMember(
  payload: Partial<OnboardBusinessRequest>,
): WidgetBusinessMember | undefined {
  return (payload.members ?? []).find((member) => member?.isPrimary);
}

/** The bank list `savings.nominated.institutions` answers, and the account a link produces. */
export const MOCK_NOMINATED_INSTITUTIONS: WidgetNominatedAccountInstitution[] = [
  {
    id: 'ins-mock-bank',
    name: 'Mock Bank',
    bic: 'MOCKGB2L',
    countries: ['GB'],
    logo: 'https://cdn.example/mock-bank.png',
  },
  {
    id: 'ins-analytical',
    name: 'Analytical Savings',
    bic: 'ANLYGB2L',
    countries: ['GB'],
    logo: 'https://cdn.example/analytical.png',
  },
];

export const MOCK_NOMINATED_ACCOUNT: WidgetNominatedAccount = {
  accountId: 'nom-1',
  accountName: 'Ada Lovelace',
  currency: 'GBP',
  country: 'GB',
  status: 'active',
  accountNumber: '55554444',
  sortCode: '04-00-04',
  institutionId: 'ins-mock-bank',
};

/** The platform's reference lists, trimmed to what an offline form needs. */
export const MOCK_LOOKUPS: Record<WidgetLookupType, WidgetLookupOption[]> = {
  'industry-categories': [
    {
      value: 'mock-software',
      label: 'Software (mock)',
      metadata: { group: 'Technology', code: 'mock-software' },
    },
  ],
  titles: [
    { value: 'mr', label: 'Mr' },
    { value: 'mrs', label: 'Mrs' },
    { value: 'ms', label: 'Ms' },
    { value: 'dr', label: 'Dr' },
  ],
  'source-of-funds': [
    { value: '1', label: 'Empl Inc Saving' },
    { value: '3', label: 'Gift' },
    { value: '4', label: 'Inheritance' },
  ],
  'account-purpose': [
    { value: '769', label: 'Everyday Savings' },
    { value: '773', label: 'Holiday' },
    { value: '774', label: 'Other' },
  ],
  countries: [
    { value: 'GB', label: 'United Kingdom' },
    { value: 'IE', label: 'Ireland' },
    { value: 'US', label: 'United States' },
  ],
  'no-tin-reasons': [
    { value: 'A', label: 'The country does not issue TINs' },
    { value: 'B', label: 'No TIN required' },
  ],
  'savings-products': [
    { value: '769', label: 'Easy Access Savings' },
    { value: '770', label: 'Fixed Term 1 year savings' },
  ],
  'savings-products-renewal': [{ value: '770', label: 'Fixed Term 1 year savings (renewal)' }],
};

/**
 * Every built-in operation the mock host answers — the same list it grants. Exported so a
 * transport can publish it as the well-known document and a spec can prove nothing is missing.
 */
export const MOCK_API_OPERATIONS = [
  'connections.connect', 'connections.status', 'connections.disconnect',
  // The project-selected reference lookups and the host-side selections. All nine are
  // answered by the switch below and always were; they were simply never listed, which is
  // the gap the `tsc -p tsconfig.spec.json` check in this library's test target now closes.
  'companies.search',
  'companies.get',
  'addresses.suggest',
  'addresses.resolve',
  'accounts.select',
  'accounts.selectCurrency',
  'beneficiaries.select',
  'cards.select',
  'savings.accounts.select',
  'payments.initiate',
  'payments.precheckInternational',
  'payments.initiateInternational',
  'payments.internalTransfer',
  'payments.reasons.list',
  'payments.schemes.list',
  'payments.scheduled.list',
  'payments.scheduled.upcoming',
  'payments.scheduled.cancel',
  'paymentRequests.list',
  'paymentRequests.get',
  'paymentRequests.create',
  'paymentRequests.approve',
  'paymentRequests.reject',
  'accounts.capabilities',
  'accounts.get',
  'accounts.create',
  'accounts.rename',
  'accounts.close',
  'exchange.rates',
  'exchange.refresh',
  'exchange.transactions.list',
  'exchange.transactions.get',
  'accounts.list',
  'transactions.list',
  'beneficiaries.list',
  'beneficiaries.create',
  'beneficiaries.createInternational',
  'beneficiaries.delete',
  'beneficiaries.cop.check',
  'beneficiaries.requirements',
  'documents.download',
  'documents.list',
  'documents.upload',
  'documents.delete',
  'support.subjects.list',
  'support.welcome.get',
  'support.threads.list',
  'support.threads.get',
  'support.threads.create',
  'support.messages.list',
  'support.messages.send',
  'support.markRead',
  'support.unreadCount',
  'support.typing',
  'companion.chat',
  'companion.decide',
  'companion.memories.list',
  'companion.memories.delete',
  'companion.memories.settings',
  'companion.actions.list',
  'companion.preferences.get',
  'companion.preferences.update',
  'companion.sessions.list',
  'companion.sessions.get',
  'companion.sessions.delete',
  'companion.insights.get',
  'rules.create',
  'rules.list',
  'rules.cancel',
  'notifications.preferences.get',
  'notifications.preferences.update',
  'notifications.list',
  'notifications.markRead',
  'notifications.registerDevice',
  'notifications.unregisterDevice',
  'bulkPayments.create',
  'bulkPayments.addBeneficiaries',
  'bulkPayments.removeTransfer',
  'bulkPayments.updatePayee',
  'bulkPayments.requestApproval',
  'bulkPayments.execute',
  'bulkPayments.cancel',
  'bulkPayments.list',
  'bulkPayments.get',
  'bulkPayments.retry',
  'cards.list',
  'cards.create',
  'cards.freeze',
  'cards.unfreeze',
  'cards.limits.get',
  'cards.wallet.beginProvisioning',
  'cards.wallet.completeProvisioning',
  'cards.secureKey',
  'cards.secureDetails',
  'cards.pin.get',
  'cards.pin.set',
  'cards.threeDsPassword.set',
  'directDebits.list',
  'directDebits.get',
  'directDebits.cancel',
  'pots.list',
  'pots.transactions.list',
  'pots.create',
  'pots.rename',
  'pots.close',
  'pots.transfer',
  'transactions.detail',
  'savings.accounts.list',
  'savings.accounts.saascada.balanceBreakdown',
  'savings.accounts.get',
  'savings.balance.total',
  'savings.accounts.page',
  'savings.transactions.list',
  'savings.requests.kyc',
  'savings.vrp.list',
  'savings.vrp.cancel',
  'savings.nominated.accounts',
  'savings.maturity.setAdvanced',
  'savings.products.list',
  'savings.products.listForCustomer',
  'savings.create',
  'savings.close',
  'savings.internalTransfer',
  'savings.withdraw',
  'savings.maturity.set',
  'savings.accounts.rename',
  'savings.requests.list',
  // The nominated account: what every savings payout already went to. `connect`/`process`
  // are the open-banking link, mocked as a bank that authorises immediately.
  'savings.nominated.get',
  'savings.nominated.institutions',
  'savings.nominated.connect',
  'savings.nominated.process',
  'savings.pisp.institutions',
  'savings.pisp.setInstitution',
  'savings.pisp.prepare',
  // The write half of the `profile` topic; patches the same fixture the topic reads.
  'profile.update',
  // The platform's reference lists.
  'lookups.list',
  'exchange.quote',
  'exchange.accept',
  'onboarding.flow',
  'onboarding.progress.save',
  'onboarding.progress.load',
  'onboarding.progress.complete',
  'onboarding.requirements',
  'onboarding.bank.verify',
  'onboarding.verify.send',
  'onboarding.verify.confirm',
  'onboarding.submitIndividual',
  'onboarding.submitBusiness',
  'onboarding.verifyIdentity',
  'identity.verification.start',
  'identity.verification.profile',
  'auth.passwordPolicy',
  'auth.status',
  'auth.login',
  'auth.logout',
  'auth.flow.start',
  'auth.flow.submit',
  'auth.flow.selectMethod',
  'auth.flow.cancel',
  'providerCapabilities.list',
  'providerCapabilities.invoke',
  'profile.changePassword',
  // The operation form of storage, granted like every other operation —
  // and, exactly as on the BFF, NOT gated by the per-definition `storage`
  // grants below: those are a widget's boundary, and an application has no
  // per-definition grant list. Seeded documents are reachable by slug.
  'records.list',
  'records.get',
  'records.create',
  'records.update',
  'records.delete',
  // The collection half of the same seeds: `storage.records`, keyed by the same
  // definition reference the documents are.
  'records.items.query',
  'records.items.get',
  'records.items.create',
  'records.items.update',
  'records.items.delete',
  // Device trust (Stage A): enrol, list and drop the customer's device keys.
  'device.enroll',
  'device.list',
  'device.remove',
  // Biometric unlock: the offline credential the vault holds, and its revocation.
  'session.biometric.enroll',
  'session.biometric.revoke',
] as const;

/**
 * The Veriff session URL the mock's `identity.verification.start` answers with. It opens
 * nothing: the mock has no vendor, so an application drives the outcome itself with
 * `setKycStatus` (see mock.md).
 */
export const MOCK_VERIFF_SESSION_URL = 'https://alchemy.veriff.com/v/mock-session';
/** The Sumsub access token the mock answers with when seeded `identity: { provider: 'sumsub' }`. */
export const MOCK_SUMSUB_ACCESS_TOKEN = 'mock-sumsub-access-token';

export function createMockWidgetHost(
  overrides: {
    context?: Partial<WidgetContext>;
    grants?: Partial<CapabilityGrants>;
    topics?: Record<string, unknown>;
    handlers?: Partial<WidgetHostHandlers>;
    /**
     * Which of the three payment outcomes `payments.initiate` answers with
     * (see PaymentInitiationStatus). Defaults to 'completed'.
     *
     * A real project's outcome is decided by *project config* — a payment
     * cancellation window, or a future-dated request — not by anything the
     * widget sends, so it is seeded here rather than derived from the payload.
     * Seed 'cancellable' to build the countdown/cancel path offline, and
     * 'scheduled' to build the "nothing has moved yet" path.
     */
    paymentOutcome?: PaymentInitiationStatus;
    /**
     * Onboarding-specific seeding. `requirements` replaces what
     * onboarding.requirements answers (the one way to exercise
     * `extraFieldsKnown: false`); `verified` pre-verifies contacts, for a test
     * that starts at the submit step instead of typing two codes.
     */
    onboarding?: {
      requirements?: WidgetOnboardingRequirements;
      verified?: OnboardingVerificationStates;
      /**
       * Whether an individual submit answers with a session, as the platform's
       * best-effort auto-login usually does. Seed `false` to build and test the
       * documented fallback: `onSuccess: 'signIn'` then resolves
       * `{ signedIn: false, redirected: true }` and nobody is signed in.
       */
      autoLoginAvailable?: boolean;
    };
    /**
     * `identity.verification.start`. `provider` is the project's configured KYC provider:
     * 'veriff' (the default) answers a launch with MOCK_VERIFF_SESSION_URL, 'sumsub' with
     * MOCK_SUMSUB_ACCESS_TOKEN, and any other name answers `{ status: 'unsupported' }` —
     * the branch a server-side AML provider produces.
     */
    identity?: { provider?: string; person?: IdentityVerificationProfile['person']; document?: IdentityVerificationProfile['document'] };
    /**
     * Session seeding. Omit it and the mock derives the session from
     * `context.user` with the same rule the real bridge uses (an id means a
     * session), so the default fixture context is a signed-in customer. Seed
     * `auth: { authenticated: false }` for a logged-out onboarding page — the
     * state a widget that calls `sdk.auth.login()` needs — and note that a
     * submit with `onSuccess: 'signIn'` flips it to true, so the whole
     * "submit → you're in" flow runs offline.
     */
    auth?: { authenticated?: boolean; userId?: string; roles?: string[] };
    /**
     * The managed login flow's script. Defaults to MOCK_AUTH_FLOW_SCRIPT
     * (username_password → sms_otp → signed in); seed `steps` to build against
     * any other shape offline — a TOTP enrolment, a "try another way" selector,
     * or a step type this SDK does not know, which is how the fail-closed branch
     * gets tested without a Keycloak.
     *
     * Completion signs the mock's session in (`auth.status` flips), so the whole
     * "log in → you're in" screen runs offline.
     */
    authFlow?: { steps?: MockAuthFlowStepSeed[] };
    storage?: {
      definitions?: DataDefinition[];
      documents?: Record<string, Record<string, unknown>>;
      /**
       * Collection rows, keyed by definition (slug or `data/<slug>`), for the
       * `records.items.*` five. Seed the matching definition with
       * `shape: 'collection'` — an unseeded or document-shaped definition is
       * what 'wrong-shape' means, and the mock enforces it rather than guessing
       * the shape from whether rows were seeded.
       *
       * Partial rows are accepted: `recordId`, `revision` and the timestamps are
       * filled in when absent, so a seed is usually just `{ value }`.
       */
      records?: Record<string, (Partial<DataRecord> & { value: Record<string, unknown> })[]>;
    };
    /**
     * The project's backend functions, by slug — there are no built-in ones to
     * default to, because a function is a thing a *project* publishes and no two
     * projects have the same set.
     *
     * Seeding a slug also grants it (see the default grants below), so building
     * against a function offline is one line. Grants are still the enforcement:
     * pass `grants: { functions: [] }` to exercise the denied branch, and call a
     * granted slug that was never seeded to get the "not activated on this
     * project" branch ('unknown-operation'), which is what production answers.
     */
    functions?: Record<string, MockFunctionSeed>;
  } = {},
): WidgetHost & {
  records: MockHostRecord[];
  forms: Record<string, unknown>;
  handlers: WidgetHostHandlers;
  /**
   * Stands in for the KYC provider's decision webhook: records `status` as the customer's
   * identity check (onboardingStatus.kyc and profile.kyc) and pushes the new onboardingStatus
   * to subscribers. The mock transport's `setKycStatus` also emits `customer.kyc.changed`.
   */
  setKycStatus(status: WidgetOnboardingCheckStatus): void;
  /**
   * Stands in for `POST /app/session/biometric/unlock`: a credential `session.biometric.enroll`
   * issued and nothing has revoked signs the customer in and answers `true`; anything else
   * answers `false`, which the mock transport turns into the BFF's 401.
   */
  redeemBiometricCredential(offlineToken: string): boolean;
} {
  const records: MockHostRecord[] = [];
  const forms: Record<string, unknown> = {};
  const supportThreads: SupportThreadDetail[] = [];
  const supportMessages: SupportMessage[] = [];
  const enrolledDevices: WidgetEnrolledDevice[] = [];
  /** Live biometric credentials, offline token → credential id. Revoking deletes the entry. */
  const biometricCredentials = new Map<string, string>();
  let biometricSequence = 0;
  const topics = { ...MOCK_TOPIC_FIXTURES, ...(overrides.topics ?? {}) };
  // The mutating operations append to / edit these lists, so give each host its
  // own copies — the module-level fixtures stay pristine.
  for (const mutated of ['accounts', 'beneficiaries', 'cards', 'savingsAccounts', 'handoffs', 'notifications']) {
    const list = topics[mutated];
    if (Array.isArray(list)) topics[mutated] = list.map((item) => ({ ...item, ...(item.balances ? { balances: item.balances.map((balance: object) => ({ ...balance })) } : {}) }));
  }
  // documents.upload/delete edit these lists, so each host gets its own copy of the map too.
  const storedDocuments: Record<string, WidgetStoredDocument[]> = Object.fromEntries(
    Object.entries(topics['storedDocuments'] as Record<string, WidgetStoredDocument[]>).map(
      ([key, list]) => [key, list.map((document) => ({ ...document }))],
    ),
  );
  let uploadedDocumentCounter = 0;
  // Savings pots against the fixture's main account: deposits debit it, withdrawals credit it.
  const pots: WidgetPot[] = [
    { potId: 'pot-1', name: 'Holiday', status: 'active', currency: 'GBP', balance: 320, availableBalance: 320, parentAccountId: 'acc-1', sortCode: '04-00-04', accountNumber: '11112222', createdAt: '2026-06-01T09:00:00.000Z' },
    { potId: 'pot-2', name: 'Rainy day', status: 'active', currency: 'GBP', balance: 1000, availableBalance: 1000, parentAccountId: 'acc-1', sortCode: '04-00-04', accountNumber: '33334444', createdAt: '2026-06-15T09:00:00.000Z' },
  ];
  const potTransactions: Record<string, WidgetPotTransaction[]> = {
    'pot-1': [{ transactionId: 'pot-txn-1', direction: 'in', amount: 320, currency: 'GBP', status: 'completed', reference: 'Holiday fund', createdAt: '2026-06-01T09:05:00.000Z' }],
    'pot-2': [{ transactionId: 'pot-txn-2', direction: 'in', amount: 1000, currency: 'GBP', status: 'completed', createdAt: '2026-06-15T09:05:00.000Z' }],
  };
  let potCounter = pots.length;
  const potsByCreateKey = new Map<string, WidgetPot>();
  /** beneficiaries.create / .createInternational / rules.create by `<operation>:<idempotencyKey>`: a repeat answers the first result. */
  const createsByKey = new Map<string, unknown>();
  const MOCK_POT_LIMIT = 10;
  const findPot = (potId: unknown): WidgetPot => {
    const pot = pots.find((candidate) => candidate.potId === potId);
    if (!pot) throw new HostError('host-error', `unknown-pot: ${String(potId)}`);
    return pot;
  };
  const potName = (operation: string, value: unknown): string => {
    const name = typeof value === 'string' ? value.trim() : '';
    if (!name || name.length > 40) throw new HostError('host-error', `${operation} requires a name of at most 40 characters`);
    return name;
  };
  // The subscriber's registered push tokens. A Set, because registering the same
  // token twice is the idempotent case the real subscriber has and the one an
  // application hits on every warm start.
  const pushTokens = new Set<string>();
  /**
   * How `storedDocuments` is addressed. `context: 'customer'` has no entity of its own — the
   * customer IS the entity — so it keys on the fixed subject the fixtures use, which is what
   * the BFF's "defaults to the session user" rule looks like offline.
   */
  const documentKey = (context: unknown, entityId: unknown): string =>
    context === 'customer' ? 'customer:me' : `${String(context ?? '')}:${String(entityId ?? '')}`;
  // payments.scheduled.cancel removes rows, so this page is per-host too.
  const scheduledPayments: WidgetPage<WidgetScheduledPayment> = {
    ...MOCK_SCHEDULED_PAYMENTS,
    items: MOCK_SCHEDULED_PAYMENTS.items.map((item) => ({ ...item })),
  };
  const paymentRequests = { ...MOCK_PAYMENT_REQUESTS, items: MOCK_PAYMENT_REQUESTS.items.map((item) => ({ ...item })) };
  // bulkPayments.retry edits rows, so each host gets its own copies too.
  const bulkPayments: WidgetPage<WidgetBulkPayment> = {
    ...MOCK_BULK_PAYMENTS,
    items: MOCK_BULK_PAYMENTS.items.map((item) => ({ ...item })),
  };
  const bulkPaymentTransfers: Record<string, WidgetBulkPaymentTransfer[]> = Object.fromEntries(
    Object.entries(MOCK_BULK_PAYMENT_TRANSFERS).map(([id, rows]) => [id, rows.map((row) => ({ ...row }))]),
  );
  let bulkPaymentCounter = 0;
  let paymentCounter = 0;
  let internationalPaymentCounter = 0;
  let quoteCounter = 0;
  // The nominated account starts CONNECTED: the state an application renders most of
  // the time, and the one `savings.withdraw` needs to be believable offline.
  let nominatedAccount: WidgetNominatedAccount | null = { ...MOCK_NOMINATED_ACCOUNT };
  let nominatedRequestCounter = 0;
  const nominatedRequests = new Set<string>();
  const quotes = new Map<string, ExchangeQuoteResult & { buyAccountId: string; sellAccountId: string }>();
  /** companion.decide: each proposal's idempotency-key attempt, moved on by a refused failure. */
  let companionPreferences = { tone: 'friendly', detail: 'balanced', financialLanguage: 'plain', homeStart: 'chat', hideBalances: false, hideAccountDetails: false };
  const companionActionLog: import('./contracts').CompanionActionLogItem[] = [];
  const proposalAttempts = new Map<string, number>();
  const exchanges: ExchangeTransactionDetail[] = [];
  const companionRules: import('./contracts').CompanionRule[] = [];
  const notificationPreferences: import('./contracts').WidgetNotificationPreferences = { accountInformation: { push: true }, marketing: { email: false, inApp: false, push: false } };
  let accountCounter = (topics['accounts'] as WidgetAccount[]).length;
  let handoffCounter = 0;
  // The mock host stands in for one widget instance, so a single scratchpad
  // object is the whole of instance scoping here.
  let widgetState: unknown;
  const dataDefinitions = overrides.storage?.definitions ?? [];
  const storageDocuments = new Map<string, DataDocument>(
    Object.entries(overrides.storage?.documents ?? {}).map(([reference, value]) => [
      reference,
      { value, revision: 1, updatedAt: new Date().toISOString() },
    ]),
  );
  const topicListeners = new Map<string, Set<(payload: unknown) => void>>();

  // ── onboarding state ──────────────────────────────────────────────────────
  // Verification lives here rather than on the topic because in production it is
  // server-side state keyed by contact value; the mock keeps the same shape (a
  // value plus an expiry) so the expiry a widget renders is a real one.
  const verifiedContacts: OnboardingVerificationStates = { ...(overrides.onboarding?.verified ?? {}) };
  /**
   * The single-use tokens a real host holds after each OTP verification and
   * presents on the submit (onboarding has no authenticated user, so the token is
   * the only thing binding the verification to this client).
   *
   * They are tracked here so the mock's submit needs one exactly as the real
   * bridge's does. They are **never** returned to the widget in either host: a
   * token that reached a bundle would be a bearer capability for the submit gate,
   * and a sandboxed frame can always exfiltrate what it holds. A seeded
   * verification counts as one that happened, so it carries a token too.
   */
  const verificationTokens: Partial<Record<WidgetVerificationChannel, string>> = {};
  for (const channel of ['email', 'phone'] as WidgetVerificationChannel[]) {
    if (verifiedContacts[channel]) verificationTokens[channel] = `mock-${channel}-verification-token`;
  }
  // Send timestamps per channel, so the mock enforces the platform's real
  // 3-per-60s limit instead of pretending sends are free.
  const otpSendTimes: Record<string, number[]> = {};
  const onboardingDrafts = new Map<string, WidgetApiOperations['onboarding.progress.load']['result']>();
  let onboardingDraftSequence = 0;
  const onboardingRequirements = overrides.onboarding?.requirements ?? { ...MOCK_ONBOARDING_REQUIREMENTS };
  const autoLoginAvailable = overrides.onboarding?.autoLoginAvailable ?? MOCK_ONBOARDING_AUTO_LOGIN_AVAILABLE;
  const seededStatus = topics['onboardingStatus'] as WidgetOnboardingStatus | undefined;
  let onboardingKyc = normaliseOnboardingCheckStatus(seededStatus?.kyc);
  let onboardingKyb = seededStatus?.kyb == null ? null : normaliseOnboardingCheckStatus(seededStatus.kyb);
  let onboardingProfileComplete = Boolean(seededStatus?.profileComplete);
  // A seeded non-'not-started' KYC/KYB is the mock's equivalent of "an
  // application already exists", exactly as the bridge derives it in production —
  // so a test can start at the identity step by seeding `kyc: 'pending'`.
  let onboardingSubmitted =
    onboardingKyc !== 'not-started' || (onboardingKyb !== null && onboardingKyb !== 'not-started');
  let onboardingKind: WidgetOnboardingKind = onboardingKyb === null ? 'individual' : 'business';

  // ── session state ─────────────────────────────────────────────────────────
  // Resolved up here because the default session is derived from context.user
  // with the SDK's shared rule — the same one the bridge applies to the
  // platform's profile — so the mock cannot invent a third notion of "signed in".
  const resolvedContext: WidgetContext = { ...MOCK_WIDGET_CONTEXT, ...(overrides.context ?? {}) };
  let authIdentity: WidgetAuthIdentity = {
    userId: resolvedContext.user?.id,
    activeRole: resolvedContext.user?.roles?.[0],
  };
  if (overrides.auth) {
    const seed = overrides.auth;
    // `authenticated: false` beats any id: it is the seed a logged-out
    // onboarding page needs, and honouring the id instead would make it a no-op.
    authIdentity =
      seed.authenticated === false
        ? {}
        : {
            userId: seed.userId ?? (seed.authenticated ? resolvedContext.user?.id : undefined),
            activeRole: seed.roles?.[0] ?? resolvedContext.user?.roles?.[0],
          };
  }

  const resolvedGrants: CapabilityGrants = {
    // NOT `Object.keys(topics)`. The fixture map is also the backing store for
    // the list operations (beneficiaries.list, cards.list, savings.*.list …),
    // so its keys are a superset of the real topics. Granting them all would
    // make the mock serve `data.get('cards')` — which no host does any more —
    // and a widget would pass here and fail in production. The registry is the
    // only list of topics that exist.
    data: Object.keys(WIDGET_TOPIC_SUPPORT),
    api: [
      ...MOCK_API_OPERATIONS,
      handoffGrant('deposits.pisp'),
    ],
    storage: { read: [], write: [] },
    // Every seeded function, and nothing else: the mock cannot grant from a
    // registry the way `data`/`api` do above, because the only functions that
    // exist here are the ones this call seeded.
    functions: Object.keys(overrides.functions ?? {}),
    forms: true,
    nav: true,
    ...(overrides.grants ?? {}),
  };

  /**
   * Recomputes the onboarding topic and stores it. `verified` and `canSubmit`
   * are always derived — never seeded — because in production they are derived
   * too, and a fixture that could disagree with the gate is exactly the
   * mock/production divergence this whole file is written to avoid.
   */
  function refreshOnboardingStatus(now: number = Date.now()): WidgetOnboardingStatus {
    const status: WidgetOnboardingStatus = {
      kyc: onboardingKyc,
      kyb: onboardingKyb,
      profileComplete: onboardingProfileComplete,
      verified: {
        email: toMockContactVerification('email', now),
        phone: toMockContactVerification('phone', now),
      },
      canSubmit:
        !onboardingSubmitted && outstandingPrerequisites({ verified: verifiedContacts, now }).length === 0,
    };
    topics['onboardingStatus'] = status;
    return status;
  }

  function toMockContactVerification(channel: WidgetVerificationChannel, now: number) {
    const state = verifiedContacts[channel];
    return isVerificationLive(state, undefined, now) && state?.expiresAt
      ? { done: true, expiresAt: state.expiresAt }
      : { done: false };
  }

  function publishOnboardingStatus(): void {
    const status = refreshOnboardingStatus();
    topicListeners.get('onboardingStatus')?.forEach((push) => push(status));
  }

  /**
   * The platform's OTP rate limit, enforced on the same numbers (3 per 60s, per
   * channel). A widget whose resend loop survives the mock survives production.
   */
  function assertMockOtpRateLimit(operation: string, channel: WidgetVerificationChannel): void {
    const now = Date.now();
    const windowMs = OTP_RATE_LIMIT.windowSeconds * 1000;
    const recent = (otpSendTimes[channel] ?? []).filter((at) => now - at < windowMs);
    otpSendTimes[channel] = recent;
    if (recent.length >= OTP_RATE_LIMIT.maxRequests) {
      const retryAfterSeconds = Math.max(1, Math.ceil((recent[0] + windowMs - now) / 1000));
      throw new HostError('rate-limited', `${operation}: too many requests for ${channel}`, {
        retryAfterSeconds,
      });
    }
    recent.push(now);
  }

  /**
   * The mock's stand-in for the host's sign-in / login-redirect mechanics.
   *
   * It records rather than navigates — there is no page to leave, and the mock
   * host is also what the app builder's canvas preview runs on, where navigating
   * would take the *builder* somewhere. That is the same trade `nav.goToPage` and
   * `handoff.start` already make, and it is what lets a widget author build the
   * "submit → you're in" screen offline: the widget stays alive and sees the
   * resolved result, which on a real host it may not.
   *
   * The `{ signedIn, redirected }` pair comes from the SDK's shared rule, so it
   * is the same pair production reports for the same situation.
   */
  function applyMockSubmitFollowUp(
    followUp: 'signIn' | 'goToLogin' | 'stay',
    session: { userId?: string; activeRole?: string },
  ): { signedIn: boolean; redirected: boolean } {
    const outcome = submitFollowUpOutcome(followUp, followUp === 'signIn' && autoLoginAvailable);
    if (outcome.signedIn) authIdentity = { ...session };
    if (followUp !== 'stay') {
      records.push({ kind: 'auth', detail: { action: followUp, ...outcome } });
    }
    return outcome;
  }

  /* ── managed login flow ─────────────────────────────────────────────────
   * The entire transaction is one index into a script. There is deliberately no
   * continuation URL anywhere in here: the mock cannot leak the one credential
   * this contract is built to keep host-side, because the contract gives it
   * nowhere to put it — and neither can the real bridge.
   */
  const authFlowScript = overrides.authFlow?.steps ?? MOCK_AUTH_FLOW_SCRIPT;
  let authFlowIndex: number | null = null;

  /** The widget's view of a script step — seeds are stripped of their scripting. */
  function authFlowStepAt(index: number, error?: WidgetAuthFlowError): WidgetAuthFlowStep {
    const seed = authFlowScript[index];
    const step: WidgetAuthFlowStep = {
      status: 'interaction-required',
      type: seed.type,
      fields: (seed.fields ?? []).map((field) => ({ ...field })),
      actions: [...(seed.actions ?? [])],
      metadata: { ...(seed.metadata ?? {}) },
    };
    // `goTo` is the script's business, not the widget's: a real method carries
    // an opaque id and a label and nothing else.
    if (seed.methods?.length) {
      step.methods = seed.methods.map(({ id, label }): WidgetAuthFlowMethod => ({ id, label }));
    }
    if (seed.expiresIn !== undefined) step.expiresIn = seed.expiresIn;
    if (error) step.error = { ...error };
    return step;
  }

  function currentAuthFlow(operation: string): { index: number; seed: MockAuthFlowStepSeed } {
    if (authFlowIndex === null) throw new HostError('host-error', authFlowNotStartedMessage(operation));
    return { index: authFlowIndex, seed: authFlowScript[authFlowIndex] };
  }

  /** Moves to `next`, or finishes the flow when the script runs out. */
  function advanceMockAuthFlow(next: number): WidgetAuthFlowState {
    if (authFlowScript[next]?.type === 'account_deleted') {
      authFlowIndex = null;
      authIdentity = {};
      records.push({ kind: 'auth', detail: { action: 'flow.complete', signedIn: false, outcome: 'account-deleted' } });
      return { status: 'complete', redirected: false, outcome: 'account-deleted' };
    }
    if (next < authFlowScript.length) {
      authFlowIndex = next;
      return authFlowStepAt(next);
    }
    authFlowIndex = null;
    // By this point the real host has exchanged the code, validated the tokens
    // and started the platform's own post-auth navigation. The mock does the one
    // part a widget can observe — there is now a session, so `auth.status` says
    // so — and records the navigation it cannot perform, exactly as `auth.login`
    // does. No token appears in the result or the record, here or in production.
    authIdentity = {
      userId: overrides.auth?.userId ?? resolvedContext.user?.id ?? MOCK_WIDGET_CONTEXT.user.id,
      activeRole: overrides.auth?.roles?.[0] ?? resolvedContext.user?.roles?.[0],
    };
    records.push({
      kind: 'auth',
      detail: { action: 'flow.complete', signedIn: true, redirected: true },
    });
    return { status: 'complete', redirected: true } satisfies WidgetAuthFlowComplete;
  }

  function assertMockPrerequisites(operation: string, email?: string, phone?: string): void {
    const outstanding = outstandingPrerequisites({ verified: verifiedContacts, email, phone });
    if (outstanding.length) {
      throw new HostError('onboarding-prerequisite-unmet', prerequisiteUnmetMessage(operation, outstanding), {
        outstanding,
      });
    }
  }

  /**
   * Storage, once. Both the `sdk.storage.*` handlers and the `records.*`
   * operations below go through these three, so a widget and a code application
   * cannot end up with two behaviours over one set of seeded documents.
   */
  const readRecord = async (reference: string): Promise<DataDocument | undefined> =>
    storageDocuments.get(reference);

  const writeRecord = async (
    reference: string,
    value: Record<string, unknown>,
    options?: { ifRevision?: number },
  ): Promise<DataDocument> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new HostError('schema-validation', `${reference} requires a JSON object`);
    }
    const current = storageDocuments.get(reference);
    if (options?.ifRevision !== undefined && options.ifRevision !== (current?.revision ?? 0)) {
      throw new HostError('conflict', `${reference} revision changed`, {
        expectedRevision: options.ifRevision,
        actualRevision: current?.revision ?? 0,
      });
    }
    const declaration = dataDefinitions.find(({ reference: candidate }) => candidate === reference);
    if (!declaration) throw new HostError('host-error', `unknown data definition: ${reference}`);
    const errors = storageSchemaErrors(declaration.schema, value);
    if (errors.length) {
      throw new HostError('schema-validation', `${reference} failed schema validation`, { errors });
    }
    const document = {
      value,
      revision: (current?.revision ?? 0) + 1,
      updatedAt: new Date().toISOString(),
    };
    storageDocuments.set(reference, document);
    return document;
  };

  const deleteRecord = async (
    reference: string,
    options?: { ifRevision?: number },
  ): Promise<void> => {
    const current = storageDocuments.get(reference);
    if (options?.ifRevision !== undefined && options.ifRevision !== (current?.revision ?? 0)) {
      throw new HostError('conflict', `${reference} revision changed`, {
        expectedRevision: options.ifRevision,
        actualRevision: current?.revision ?? 0,
      });
    }
    storageDocuments.delete(reference);
  };

  /* ── collections (`records.items.*`) ──────────────────────────────────────
   *
   * The store itself is mock-collections.ts; what stays here is the wiring.
   * `subjectOf` is read on every call rather than captured once, because the
   * mock's session moves under it: a login, a logout or a completed auth flow
   * changes whose rows a user-scoped definition holds.
   */
  const collections = createMockCollections({
    definitions: dataDefinitions,
    seeds: overrides.storage?.records,
    subjectOf: () => authIdentity.userId,
  });

  const handlers: WidgetHostHandlers = {
    data: {
      // onboardingStatus is derived, so it is recomputed on read rather than
      // served from whatever the last write left behind (verification expires
      // between reads with nothing happening in between).
      get: async (topic) => (topic === 'onboardingStatus' ? refreshOnboardingStatus() : topics[topic]),
      subscribe: (topic, push) => {
        if (topic === 'onboardingStatus') refreshOnboardingStatus();
        push(topics[topic]);
        let listeners = topicListeners.get(topic);
        if (!listeners) topicListeners.set(topic, (listeners = new Set()));
        listeners.add(push);
        return () => void listeners?.delete(push);
      },
    },
    handoff: {
      // No vendor to redirect to and no page to leave, so the mock resolves the
      // whole round trip in one call: the record lands 'completed' and the
      // handoffs topic reflects it immediately. A widget written against this
      // still reads the outcome from the topic, which is the flow that runs
      // against the real host.
      start: async (kind, params) => {
        const handoffId = `handoff-${++handoffCounter}`;
        records.push({ kind: 'handoff', detail: { handoffId, kind, params } });
        const handoffs = topics['handoffs'] as WidgetHandoffRecord[];
        handoffs.push({
          handoffId,
          kind,
          status: 'completed',
          params,
          result: { status: 'completed' },
          startedAt: new Date().toISOString(),
        });
        topicListeners.get('handoffs')?.forEach((push) => push(handoffs));
        return { handoffId };
      },
    },
    state: {
      get: async () => widgetState,
      set: async (value) => void (widgetState = value),
    },
    storage: {
      get: readRecord,
      set: writeRecord,
      remove: deleteRecord,
      catalog: async () => dataDefinitions.filter(({ access }) => access.read || access.write),
    },
    functions: {
      invoke: async (slug, payload) => {
        records.push({ kind: 'function', detail: { slug, payload } });
        const fixtures = overrides.functions ?? {};
        // Granted but not seeded: the production answer to a slug no function is
        // activated for, not a silent undefined. `in`, not truthiness — a seeded
        // constant of null/0/false is a legitimate result.
        if (!(slug in fixtures)) {
          throw new HostError('unknown-operation', `no function activated for slug: ${slug}`);
        }
        const fixture = fixtures[slug];
        const outcome = typeof fixture === 'function' ? await fixture(payload) : fixture;
        const failure = (
          outcome as {
            error?: { code?: WidgetErrorCode; message?: string; details?: Record<string, unknown> };
          }
        )?.error;
        if (outcome && typeof outcome === 'object' && failure) {
          throw new HostError(
            failure.code ?? 'host-error',
            failure.message ?? `${slug} failed`,
            failure.details,
          );
        }
        return outcome;
      },
    },
    api: {
      call: async (operation, payload) => {
        records.push({ kind: 'api', detail: { operation, payload } });
        switch (operation) {
          case 'companies.search': {
            const name = String(payload['companyName'] ?? '')
              .trim()
              .toLowerCase();
            const location = String(payload['location'] ?? '')
              .trim()
              .toLowerCase();
            if (!name && !location) {
              throw new HostError('host-error', 'companies.search requires companyName or location');
            }
            const matches = MOCK_COMPANIES.filter(
              (company) =>
                (!name || company.companyName.toLowerCase().includes(name)) &&
                (!location ||
                  Object.values(company.registeredOfficeAddress ?? {}).some((value) =>
                    String(value).toLowerCase().includes(location),
                  )),
            );
            const page = Math.max(1, Number(payload['page'] ?? 1));
            const size = Math.min(20, Math.max(1, Number(payload['size'] ?? 10)));
            return {
              items: matches.slice((page - 1) * size, page * size),
              page,
              totalItems: matches.length,
            } satisfies WidgetPage<WidgetCompanySummary>;
          }
          case 'companies.get': {
            const companyNumber = String(payload['companyNumber'] ?? '').trim();
            const company = MOCK_COMPANY_DETAILS.find(
              (candidate) => candidate.companyNumber === companyNumber,
            );
            if (!company) throw new HostError('host-error', `company not found: ${companyNumber}`);
            return company;
          }
          case 'addresses.suggest': {
            const query = String(payload['query'] ?? '')
              .trim()
              .toLowerCase();
            if (query.length < 3) {
              throw new HostError('host-error', 'addresses.suggest query must be at least 3 characters');
            }
            return MOCK_ADDRESS_SUGGESTIONS.filter((suggestion) =>
              suggestion.label.toLowerCase().includes(query),
            );
          }
          case 'addresses.resolve': {
            const id = String(payload['id'] ?? '');
            const address = MOCK_RESOLVED_ADDRESSES[id];
            if (!address) throw new HostError('host-error', `address not found: ${id}`);
            return address;
          }
          case 'payments.initiate': {
            // Built as the wire body the real host receives and then run
            // through the shared derivation, so the mock cannot disagree with
            // production about which outcome a body means.
            const wire: Record<string, unknown> = {
              id: `pay-${++paymentCounter}`,
              amount: Number(payload['amount'] ?? 0),
              currency: String(payload['currency'] ?? 'GBP'),
              paymentReference: String(payload['reference'] ?? ''),
            };
            // A future-dated payload schedules exactly as production does: the
            // hosts' shared date rule gates it, and the platform's response
            // echoes the raw date as `scheduledAt` (which is what makes the
            // derived status 'scheduled').
            if (
              payload['scheduledAt'] !== undefined &&
              payload['scheduledAt'] !== null &&
              payload['scheduledAt'] !== ''
            ) {
              const problem = invalidScheduledAtReason(String(payload['scheduledAt']));
              if (problem) throw new HostError('host-error', `payments.initiate ${problem}`);
              wire['scheduledAt'] = String(payload['scheduledAt']);
            }
            if (overrides.paymentOutcome === 'cancellable') {
              // A cancellation-window project stamps expiryReversalAt on top of
              // any schedule date — both ride the body and 'cancellable' wins,
              // same as production's derivation order.
              wire['expiryReversalAt'] = new Date(Date.now() + 60_000).toISOString();
            } else if (overrides.paymentOutcome === 'scheduled' && !wire['scheduledAt']) {
              wire['scheduledAt'] = new Date(Date.now() + 86_400_000).toISOString();
            }
            return {
              ...wire,
              transactionId: String(wire['id']),
              status: derivePaymentInitiationStatus(wire),
            } satisfies PaymentInitiationResult;
          }
          case 'payments.internalTransfer': {
            // The platform's rules: two different accounts of the organisation, the
            // source account's currency on both sides, a positive amount.
            const accounts = topics['accounts'] as WidgetAccount[];
            const fromAccountId = String(payload['fromAccountId'] ?? '');
            const toAccountId = String(payload['toAccountId'] ?? '');
            const amount = Number(payload['amount'] ?? 0);
            if (!Number.isFinite(amount) || amount <= 0) {
              throw new HostError('invalid-payload', 'payments.internalTransfer amount must be a positive number');
            }
            if (fromAccountId === toAccountId) {
              throw new HostError('invalid-payload', 'a transfer must name two different accounts');
            }
            const from = accounts.find((candidate) => candidate.accountId === fromAccountId);
            const to = accounts.find((candidate) => candidate.accountId === toAccountId);
            if (!from || !to) throw new HostError('host-error', 'Account not found.');
            if (from.currency !== to.currency) {
              throw new HostError('invalid-payload', 'payments.internalTransfer accounts must share a currency');
            }
            if (from.availableBalance < amount) throw new HostError('host-error', 'insufficient-balance');
            from.availableBalance = Number((from.availableBalance - amount).toFixed(2));
            to.availableBalance = Number((to.availableBalance + amount).toFixed(2));
            return {
              fromAccountId,
              toAccountId,
              amount,
              transactionId: `pay-${++paymentCounter}`,
            } satisfies InternalTransferResult;
          }
          case 'beneficiaries.cop.check':
            return mockCopCheck(payload, topics['beneficiaries'] as WidgetBeneficiary[]);
          case 'beneficiaries.list': {
            // Filters mirror the platform's: name/accountNumber substring
            // (name case-insensitive), currency exact. Serves pages of the
            // `beneficiaries` topic so a listing widget's paging/search UI is
            // testable offline. `country` is not on WidgetBeneficiary, so the
            // mock ignores it — as an all-domestic dataset genuinely would.
            const name = payload['name'] ? String(payload['name']).toLowerCase() : undefined;
            const accountNumber = payload['accountNumber'] ? String(payload['accountNumber']) : undefined;
            const currency = payload['currency'] ? String(payload['currency']) : undefined;
            const matches = (topics['beneficiaries'] as WidgetBeneficiary[]).filter(
              (candidate) =>
                (!name || candidate.name.toLowerCase().includes(name)) &&
                (!accountNumber || (candidate.accountNumber ?? '').includes(accountNumber)) &&
                (!currency || candidate.currency === currency),
            );
            const page = Math.max(1, Number(payload['page'] ?? 1));
            const size = Math.max(1, Number(payload['size'] ?? 20));
            return {
              items: matches.slice((page - 1) * size, page * size),
              page,
              totalItems: matches.length,
            } satisfies WidgetPage<WidgetBeneficiary>;
          }
          case 'beneficiaries.select':
          case 'accounts.select':
          case 'accounts.selectCurrency':
          case 'cards.select':
          case 'savings.accounts.select':
            // App-side page state only; the api record above is the assertion
            // surface for the whole selection family. Unknown ids resolve here
            // even where the real host refuses them (accounts, cards) — the
            // mock has no page state to guard.
            return { selected: true };
          case 'payments.reasons.list':
            return MOCK_TRANSFER_REASONS;
          case 'beneficiaries.requirements':
            return MOCK_BENEFICIARY_REQUIREMENTS;
          case 'beneficiaries.create': {
            const payeeKey = typeof payload['idempotencyKey'] === 'string' ? `beneficiaries.create:${payload['idempotencyKey']}` : undefined;
            if (payeeKey && createsByKey.has(payeeKey)) return structuredClone(createsByKey.get(payeeKey)) as BeneficiaryCreateResult;
            const beneficiaries = topics['beneficiaries'] as WidgetBeneficiary[];
            const created: WidgetBeneficiary = {
              beneficiaryId: `ben-${beneficiaries.length + 1}`,
              name: String(payload['name'] ?? ''),
            };
            if (payload['accountNumber']) created.accountNumber = String(payload['accountNumber']);
            if (payload['sortCode']) created.sortCode = String(payload['sortCode']);
            if (payload['iban']) created.iban = String(payload['iban']);
            if (payload['currency']) created.currency = String(payload['currency']);
            beneficiaries.push(created);
            if (payeeKey) createsByKey.set(payeeKey, { beneficiaryId: created.beneficiaryId });
            return { beneficiaryId: created.beneficiaryId } satisfies BeneficiaryCreateResult;
          }
          case 'beneficiaries.delete': {
            const beneficiaries = topics['beneficiaries'] as WidgetBeneficiary[];
            const beneficiaryId = String(payload['beneficiaryId'] ?? '');
            const index = beneficiaries.findIndex((candidate) => candidate.beneficiaryId === beneficiaryId);
            if (index < 0) throw new HostError('host-error', `beneficiary-not-found: ${beneficiaryId}`);
            beneficiaries.splice(index, 1);
            return { beneficiaryId, deleted: true } satisfies WidgetApiOperations['beneficiaries.delete']['result'];
          }
          // accounts.list and transactions.list back what used to be the
          // `accounts` and `transactions` data topics. Both page the fixtures
          // the same way beneficiaries.list does, so a listing widget's paging
          // UI is testable offline rather than always seeing the whole set.
          case 'accounts.capabilities':
            return {
              provider: 'universal-sandbox', supportsAccountV2: true,
              supportedRelationshipTypes: ['standalone'],
              supportedAccountModels: ['single-currency', 'multi-currency'], supportedOwnerTypes: ['organisation', 'person'],
              requiresCurrencies: true, requiresPrimaryCurrency: false, requiresPersonIdForPersonOwner: false,
              supportsRequestedCapabilities: { payments: true, cards: true, virtualCards: true, physicalCards: true },
            };
          case 'accounts.create': {
            const v2 = payload['accountModel'] !== undefined;
            const currencies = v2 ? payload['currencies'] : [payload['currency']];
            const name = payload['name'];
            if (!Array.isArray(currencies) || !currencies.length || !currencies.every((currency) => typeof currency === 'string' && /^[A-Z]{3}$/.test(currency)) ||
                new Set(currencies).size !== currencies.length ||
                (name !== undefined && (typeof name !== 'string' || !name.trim())) ||
                (v2 && (typeof name !== 'string' || !['single-currency', 'multi-currency'].includes(String(payload['accountModel'])) ||
                  payload['relationshipType'] !== 'standalone')) ||
                (payload['accountModel'] === 'single-currency' && currencies.length !== 1) ||
                (payload['primaryCurrency'] !== undefined && !currencies.includes(payload['primaryCurrency']))) {
              throw new HostError('invalid-payload', 'Provide an account name, valid model and matching currency codes.');
            }
            const accounts = topics['accounts'] as WidgetAccount[];
            if (payload['relationshipType'] === 'child' && !accounts.some((account) => account.accountId === payload['parentAccountId'])) throw new HostError('invalid-payload', 'Select a parent account.');
            const currency = String(payload['primaryCurrency'] ?? currencies[0]);
            const account: WidgetAccount = {
              accountId: `acc-${++accountCounter}`, name: String(name ?? `${currency} Account`), currency, availableBalance: 0,
              accountType: payload['accountModel'] === 'multi-currency' ? 'multi-currency' : 'current',
              currencies: [...currencies], balances: currencies.map((currency) => ({ currency, balance: 0, availableBalance: 0 })),
              ...(typeof payload['parentAccountId'] === 'string' ? { parentAccountId: payload['parentAccountId'] } : {}), status: 'active',
            };
            accounts.push(account);
            return { ...account };
          }
          case 'accounts.get':
          case 'accounts.rename':
          case 'accounts.close': {
            const accounts = topics['accounts'] as WidgetAccount[];
            const account = accounts.find((item) => item.accountId === payload['accountId']);
            if (!account) throw new HostError('host-error', 'Account not found.');
            if (operation === 'accounts.get') return { ...account };
            if (operation === 'accounts.rename') {
              if (typeof payload['name'] !== 'string' || !payload['name'].trim()) throw new HostError('invalid-payload', 'Enter an account name.');
              account.name = payload['name'];
              return { updated: true };
            }
            if (account.availableBalance !== 0 || account.balances?.some((balance) => [balance.balance, balance.availableBalance, balance.indicativeBalance, balance.ledgerBalance, balance.ledgerBalanceIncludingPendingExchange].some((amount) => amount !== undefined && amount !== 0))) throw new HostError('host-error', 'Account balance must be zero before closure.');
            topics['accounts'] = accounts.filter((item) => item !== account);
            return { success: true };
          }
          case 'accounts.list': {
            const all = topics['accounts'] as WidgetAccount[];
            const page = Math.max(1, Number(payload['page'] ?? 1));
            const size = Math.max(1, Number(payload['size'] ?? 20));
            return {
              items: all.slice((page - 1) * size, page * size),
              page,
              totalItems: all.length,
            } satisfies WidgetPage<WidgetAccount>;
          }
          case 'transactions.list': {
            // accountId is accepted and deliberately ignored: the fixture set is
            // one account's history, and pretending to filter would imply a
            // scoping rule the mock does not actually model.
            const all = topics['transactions'] as WidgetTransaction[];
            const page = Math.max(1, Number(payload['page'] ?? 1));
            const size = Math.max(1, Number(payload['size'] ?? 20));
            return {
              items: all.slice((page - 1) * size, page * size),
              page,
              totalItems: all.length,
            } satisfies WidgetPage<WidgetTransaction>;
          }
          case 'cards.list':
            return topics['cards'];
          case 'transactions.detail':
            return {
              ...MOCK_TRANSACTION_DETAIL,
              transactionId: String(payload['transactionId'] ?? MOCK_TRANSACTION_DETAIL.transactionId),
            };
          case 'payments.scheduled.list':
            return scheduledPayments;
          case 'payments.scheduled.upcoming': {
            // The fixture keeps one set of rows; a future-dated row is one that carries
            // `scheduledAt`, which is the only thing that tells the two listings apart offline.
            const accountId = payload['accountId'] ? String(payload['accountId']) : undefined;
            const items = scheduledPayments.items.filter(
              (item) => item.scheduledAt && (!accountId || item.sourceAccountId === accountId),
            );
            return { items, page: 1, totalItems: items.length } satisfies WidgetPage<WidgetScheduledPayment>;
          }
          case 'payments.scheduled.cancel': {
            const paymentId = String(payload['paymentId'] ?? '');
            const index = scheduledPayments.items.findIndex((item) => item.paymentId === paymentId);
            if (index < 0) throw new HostError('host-error', `unknown-payment: ${paymentId}`);
            scheduledPayments.items.splice(index, 1);
            scheduledPayments.totalItems = scheduledPayments.items.length;
            return { paymentId };
          }
          case 'paymentRequests.list': {
            const page = Math.max(1, Number(payload['page'] ?? 1));
            const size = Math.max(1, Number(payload['size'] ?? 20));
            const query = String(payload['query'] ?? '').toLowerCase();
            const rows = paymentRequests.items.filter((item) =>
              (!payload['status'] || item.status === payload['status']) &&
              `${item.reference ?? ''} ${item.requesterName ?? ''} ${item.beneficiaryName ?? ''}`.toLowerCase().includes(query),
            );
            return { items: rows.slice((page - 1) * size, page * size), page, totalItems: rows.length };
          }
          case 'paymentRequests.get': {
            const id = payload['paymentRequestId'];
            if (typeof id !== 'string' || !id.trim()) throw new HostError('host-error', 'paymentRequestId is required');
            const row = paymentRequests.items.find((request) => request.paymentRequestId === id || request.approvalRecordId === id);
            if (!row) throw new HostError('host-error', `unknown-payment-request: ${id}`);
            return { ...row };
          }
          case 'paymentRequests.approve':
          case 'paymentRequests.reject': {
            const ids = payload['recordIds'];
            if (!Array.isArray(ids) || !ids.length || ids.some((id) => typeof id !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id))) {
              throw new HostError('host-error', 'recordIds requires one or more UUID approval record IDs');
            }
            const success: { id: string }[] = [];
            const failed: { id: string; error: string }[] = [];
            for (const id of ids) {
              const row = paymentRequests.items.find((item) => item.approvalRecordId === id);
              if (!row || row.status !== 'pending') {
                failed.push({ id, error: row ? 'Payment request is not pending' : 'Approval record not found' });
                continue;
              }
              row.status = operation === 'paymentRequests.approve' ? 'approved' : 'rejected';
              row.approvedOrRejectedDate = new Date().toISOString();
              success.push({ id });
            }
            if (!success.length) throw new HostError('conflict', `No payment requests could be ${operation === 'paymentRequests.approve' ? 'approved' : 'rejected'}`, { result: { success, failed } });
            return { success, failed };
          }
          case 'cards.limits.get':
            return MOCK_CARD_LIMITS;
          // End-to-end encrypted card data needs a provider holding the private key; the offline
          // fixture has none, so reveals are refused and the writes are accepted unread.
          case 'cards.secureKey':
          case 'cards.secureDetails':
          case 'cards.pin.get':
            throw new HostError('host-error', `${operation}: the offline fixture has no end-to-end encrypted card data`);
          case 'cards.pin.set':
          case 'cards.threeDsPassword.set':
            return { cardId: String(payload['cardId'] ?? ''), updated: true };
          case 'savings.accounts.saascada.balanceBreakdown':
            return { available: false, savingsAccountId: String(payload['savingsAccountId']), reason: 'No provider transaction history in mock host' };
          case 'savings.accounts.get': {
            const account = (topics['savingsAccounts'] as WidgetSavingsAccount[]).find((row) => row.savingsAccountId === payload['savingsAccountId']);
            if (!account) throw new Error('Savings account not found');
            return account;
          }
          case 'savings.balance.total': {
            const currency = String(payload['currency']).toUpperCase();
            const accounts = topics['savingsAccounts'] as WidgetSavingsAccount[];
            return { currency, amount: accounts.filter((account) => account.currency === currency).reduce((total, account) => total + (account.balance ?? 0), 0) };
          }
          case 'savings.accounts.page': {
            const accounts = topics['savingsAccounts'] as WidgetSavingsAccount[];
            const page = Number(payload['page'] ?? 1);
            const size = Number(payload['size'] ?? 50);
            return { items: accounts.slice((page - 1) * size, page * size), page, totalItems: accounts.length };
          }
          case 'savings.transactions.list':
            return { items: [], page: Number(payload['page'] ?? 1), totalItems: 0 };
          case 'savings.requests.kyc':
            return { status: 'manual-review' };
          case 'savings.vrp.list':
          case 'savings.nominated.accounts':
            return [];
          case 'savings.vrp.cancel':
            throw new Error('No mandate exists in the offline fixture');
          case 'pots.list':
            return pots.filter((pot) => pot.status !== 'closed').map((pot) => ({ ...pot }));
          case 'pots.transactions.list': {
            const pot = findPot(payload['potId']);
            const page = Number(payload['page'] ?? 1);
            const size = Number(payload['size'] ?? 20);
            const all = potTransactions[pot.potId] ?? [];
            return { items: all.slice((page - 1) * size, page * size), page, size, hasMore: page * size < all.length };
          }
          case 'pots.create': {
            const name = potName('pots.create', payload['name']);
            const createKey = typeof payload['idempotencyKey'] === 'string' ? payload['idempotencyKey'] : undefined;
            const replayed = createKey ? potsByCreateKey.get(createKey) : undefined;
            if (replayed) return { ...replayed };
            if (pots.filter((pot) => pot.status !== 'closed').length >= MOCK_POT_LIMIT) {
              throw new HostError('conflict', 'pot-limit-reached', undefined, 'pot-limit-reached');
            }
            potCounter += 1;
            const pot: WidgetPot = { potId: `pot-${potCounter}`, name, status: 'active', currency: 'GBP', balance: 0, availableBalance: 0, parentAccountId: 'acc-1', createdAt: new Date().toISOString() };
            pots.push(pot);
            potTransactions[pot.potId] = [];
            if (createKey) potsByCreateKey.set(createKey, pot);
            return { ...pot };
          }
          case 'pots.rename': {
            const pot = findPot(payload['potId']);
            pot.name = potName('pots.rename', payload['name']);
            return { ...pot };
          }
          case 'pots.close': {
            const pot = findPot(payload['potId']);
            if (pot.status !== 'active') throw new HostError('conflict', 'pot-not-active', undefined, 'pot-not-active');
            if ((pot.balance ?? 0) !== 0) throw new HostError('conflict', 'pot-not-empty', undefined, 'pot-not-empty');
            pot.status = 'closed';
            return { ...pot };
          }
          case 'pots.transfer': {
            const pot = findPot(payload['potId']);
            const direction = payload['direction'];
            const amount = Number(payload['amount']);
            if (direction !== 'deposit' && direction !== 'withdraw') {
              throw new HostError('host-error', "pots.transfer direction must be 'deposit' or 'withdraw'");
            }
            if (!Number.isFinite(amount) || amount <= 0 || Math.round(amount * 100) !== amount * 100) {
              throw new HostError('host-error', 'pots.transfer amount must be greater than 0 with at most 2 decimal places');
            }
            const parent = (topics['accounts'] as WidgetAccount[]).find((account) => account.accountId === pot.parentAccountId);
            const available = direction === 'deposit' ? (parent?.availableBalance ?? 0) : (pot.balance ?? 0);
            if (available < amount) throw new HostError('host-error', 'insufficient-funds', undefined, 'insufficient-funds');
            const signed = direction === 'deposit' ? amount : -amount;
            pot.balance = Number(((pot.balance ?? 0) + signed).toFixed(2));
            pot.availableBalance = pot.balance;
            if (parent) parent.availableBalance = Number((parent.availableBalance - signed).toFixed(2));
            const transactionId = `pot-txn-${Date.now()}`;
            const reference = typeof payload['reference'] === 'string' ? payload['reference'] : undefined;
            (potTransactions[pot.potId] ??= []).unshift({ transactionId, direction: direction === 'deposit' ? 'in' : 'out', amount, currency: pot.currency, status: 'completed', ...(reference ? { reference } : {}), createdAt: new Date().toISOString() });
            return { transactionId, potId: pot.potId, direction, amount, currency: pot.currency } satisfies PotTransferResult;
          }
          case 'directDebits.list':
            return { items: [], page: Number(payload['page'] ?? 1), totalItems: 0 };
          case 'directDebits.get':
          case 'directDebits.cancel':
            throw new Error('No Direct Debit mandate exists in the offline fixture');
          case 'savings.maturity.setAdvanced':
            return { savingsAccountId: payload['savingsAccountId'] };
          case 'savings.accounts.list':
            return topics['savingsAccounts'];
          case 'savings.products.list':
            return payload['category'] === 'renewal'
              ? MOCK_SAVINGS_RENEWAL_PRODUCTS
              : topics['savingsProducts'];
          // No provider filters by organisation yet, so the per-customer
          // catalogue is the same array here as it is in production.
          case 'savings.products.listForCustomer':
            return topics['savingsProducts'];
          case 'savings.create': {
            const accounts = topics['savingsAccounts'] as WidgetSavingsAccount[];
            const products = topics['savingsProducts'] as WidgetSavingsProduct[];
            const product = products.find((candidate) => candidate.productCode === payload['productCode']);
            const created: WidgetSavingsAccount = {
              savingsAccountId: `sav-${accounts.length + 1}`,
              currency: String(payload['currency'] ?? product?.currency ?? 'GBP'),
              balance: 0,
              status: 'active',
            };
            const productName = payload['accountName'] ?? product?.productName;
            if (productName) created.productName = String(productName);
            const accountType = payload['accountType'] ?? product?.accountType;
            if (accountType) created.accountType = String(accountType);
            if (product?.interestRate !== undefined) created.interestRate = product.interestRate;
            accounts.push(created);
            return {
              savingsAccountId: created.savingsAccountId,
              status: 'approved',
              applicationNumber: `APP-${accounts.length}`,
            } satisfies SavingsAccountCreateResult;
          }
          case 'savings.close': {
            const accounts = topics['savingsAccounts'] as WidgetSavingsAccount[];
            const savingsAccountId = String(payload['savingsAccountId'] ?? '');
            const index = accounts.findIndex((candidate) => candidate.savingsAccountId === savingsAccountId);
            if (index < 0) throw new HostError('host-error', `unknown-savings-account: ${savingsAccountId}`);
            accounts.splice(index, 1);
            return { savingsAccountId } satisfies SavingsAccountCloseResult;
          }
          case 'savings.internalTransfer': {
            const accounts = topics['savingsAccounts'] as WidgetSavingsAccount[];
            const fromSavingsAccountId = String(payload['fromSavingsAccountId'] ?? '');
            const toSavingsAccountId = String(payload['toSavingsAccountId'] ?? '');
            const amount = Number(payload['amount'] ?? 0);
            const from = accounts.find((candidate) => candidate.savingsAccountId === fromSavingsAccountId);
            const to = accounts.find((candidate) => candidate.savingsAccountId === toSavingsAccountId);
            if (!from || !to) throw new HostError('host-error', 'unknown-savings-account');
            // The platform's own capability gate (SavingsService): blocked only
            // on an explicit false, absence is permitted.
            if (from.isWithdrawalEnabled === false) {
              throw new HostError('host-error', `withdrawal-disabled: ${fromSavingsAccountId}`);
            }
            if (to.isFundingEnabled === false) {
              throw new HostError('host-error', `funding-disabled: ${toSavingsAccountId}`);
            }
            if (from.balance < amount) throw new HostError('host-error', 'insufficient-balance');
            from.balance = Number((from.balance - amount).toFixed(2));
            to.balance = Number((to.balance + amount).toFixed(2));
            if (from.availableBalance !== undefined) {
              from.availableBalance = Number((from.availableBalance - amount).toFixed(2));
            }
            if (to.availableBalance !== undefined) {
              to.availableBalance = Number((to.availableBalance + amount).toFixed(2));
            }
            return {
              fromSavingsAccountId,
              toSavingsAccountId,
              amount,
            } satisfies SavingsInternalTransferResult;
          }
          case 'savings.withdraw': {
            const accounts = topics['savingsAccounts'] as WidgetSavingsAccount[];
            const savingsAccountId = String(payload['savingsAccountId'] ?? '');
            const amount = Number(payload['amount'] ?? 0);
            const account = accounts.find((candidate) => candidate.savingsAccountId === savingsAccountId);
            if (!account) throw new HostError('host-error', `unknown-savings-account: ${savingsAccountId}`);
            if (!Number.isFinite(amount) || amount <= 0) {
              throw new HostError('host-error', 'savings.withdraw amount must be a positive number');
            }
            // The provider's gate (the platform's withdraw route is a pure
            // pass-through); like the platform's own guards, only an explicit
            // false blocks.
            if (account.isWithdrawalEnabled === false) {
              throw new HostError('host-error', `withdrawal-disabled: ${savingsAccountId}`);
            }
            // The native flow validates against availableBalance, not balance.
            const available = account.availableBalance ?? account.balance;
            if (amount > available) throw new HostError('host-error', 'insufficient-balance');
            if (account.accountType === 'notice') {
              // A notice account moves nothing now: the amount is reserved
              // against the notice and leaves after the notice period — so
              // availableBalance drops and balance does not, which is exactly
              // what a re-read of the account shows on the real providers.
              if (account.availableBalance !== undefined) {
                account.availableBalance = Number((account.availableBalance - amount).toFixed(2));
              }
              return {
                savingsAccountId,
                amount,
                outcome: 'notice-submitted',
              } satisfies SavingsWithdrawResult;
            }
            account.balance = Number((account.balance - amount).toFixed(2));
            if (account.availableBalance !== undefined) {
              account.availableBalance = Number((account.availableBalance - amount).toFixed(2));
            }
            return { savingsAccountId, amount, outcome: 'withdrawn' } satisfies SavingsWithdrawResult;
          }
          case 'savings.accounts.rename': {
            const accounts = topics['savingsAccounts'] as WidgetSavingsAccount[];
            const savingsAccountId = String(payload['savingsAccountId'] ?? '');
            const accountName = String(payload['accountName'] ?? '');
            const account = accounts.find((candidate) => candidate.savingsAccountId === savingsAccountId);
            if (!account) throw new HostError('host-error', `unknown-savings-account: ${savingsAccountId}`);
            if (!accountName) {
              throw new HostError('host-error', 'savings.accounts.rename requires accountName');
            }
            // Mutated in the fixture store, so a listing re-read after the
            // rename shows the new label — the whole point of the operation.
            account.accountName = accountName;
            return { savingsAccountId, accountName } satisfies SavingsAccountRenameResult;
          }
          case 'savings.requests.list': {
            const status = payload['status'];
            return status
              ? MOCK_SAVINGS_ACCOUNT_REQUESTS.filter((request) => request.status === status)
              : MOCK_SAVINGS_ACCOUNT_REQUESTS;
          }
          case 'savings.maturity.set': {
            const accounts = topics['savingsAccounts'] as WidgetSavingsAccount[];
            const savingsAccountId = String(payload['savingsAccountId'] ?? '');
            const decision = payload['decision'] as SavingsMaturitySetRequest['decision'];
            const account = accounts.find((candidate) => candidate.savingsAccountId === savingsAccountId);
            if (!account) throw new HostError('host-error', `unknown-savings-account: ${savingsAccountId}`);
            if (decision !== 'renew' && decision !== 'withdraw' && decision !== 'partial-reinvest') {
              throw new HostError(
                'host-error',
                "savings.maturity.set decision must be 'renew', 'withdraw' or 'partial-reinvest'",
              );
            }
            if ((decision === 'renew' || decision === 'partial-reinvest') && !payload['productCode']) {
              throw new HostError('host-error', `savings.maturity.set '${decision}' requires productCode`);
            }
            if (decision === 'partial-reinvest') {
              const reinvestAmount = Number(payload['reinvestAmount']);
              if (!Number.isFinite(reinvestAmount) || reinvestAmount <= 0) {
                throw new HostError(
                  'host-error',
                  "savings.maturity.set 'partial-reinvest' requires a positive reinvestAmount",
                );
              }
            }
            // Nothing moves and nothing on the account changes now — the
            // decision applies at term end, and the mock, like the platform,
            // does not gate on isRenewalEnabled (that rejection is the
            // provider's). The api record above is the assertion surface.
            return { savingsAccountId, decision } satisfies SavingsMaturitySetResult;
          }
          /* ─── the nominated account (mock) ───────────────────────────
           * One connected GB account, and a bank that authorises immediately:
           * `connect` mints a request and `process` answers 'connected' for it.
           * Deliberately never 'many-accounts' by default — the choice path is
           * the rarer one and a fixture that always took it would make every
           * offline run exercise it.
           */
          case 'savings.nominated.get':
            return nominatedAccount;
          case 'savings.nominated.institutions':
            return MOCK_NOMINATED_INSTITUTIONS.filter(
              (institution) =>
                !institution.countries ||
                institution.countries.includes(String(payload['countryCode'] ?? 'GB')),
            );
          case 'savings.pisp.institutions':
            return MOCK_NOMINATED_INSTITUTIONS;
          case 'savings.pisp.setInstitution': {
            const institutionId = payload['institutionId'];
            if (typeof institutionId !== 'string' || !institutionId) {
              throw new HostError('invalid-payload', 'savings.pisp.setInstitution requires institutionId');
            }
            nominatedAccount = { ...(nominatedAccount ?? MOCK_NOMINATED_ACCOUNT), institutionId };
            return nominatedAccount;
          }
          case 'savings.pisp.prepare': {
            const amount = Number(payload['amount']);
            if (!Number.isFinite(amount) || amount <= 0) {
              throw new HostError('invalid-payload', 'savings.pisp.prepare requires a positive amount');
            }
            return { billingRequestId: `BRQ-mock-${Math.round(amount * 100)}`, environment: 'sandbox' };
          }
          case 'savings.nominated.connect': {
            const institutionId = String(payload['institutionId'] ?? '');
            if (!institutionId) {
              throw new HostError('invalid-payload', 'savings.nominated.connect requires institutionId');
            }
            const redirectUrl = String(payload['redirectUrl'] ?? '');
            if (!/^https?:\/\//i.test(redirectUrl)) {
              throw new HostError(
                'invalid-payload',
                'savings.nominated.connect requires an http(s) redirectUrl',
              );
            }
            const requestId = `nom-req-${++nominatedRequestCounter}`;
            nominatedRequests.add(requestId);
            return {
              requestId,
              url: `https://bank.example/authorise/${requestId}`,
            } satisfies NominatedAccountConnectResult;
          }
          case 'savings.nominated.process': {
            const requestId = String(payload['requestId'] ?? '');
            if (!requestId) {
              throw new HostError('invalid-payload', 'savings.nominated.process requires requestId');
            }
            if (!nominatedRequests.has(requestId)) {
              return { status: 'not-connected' } satisfies NominatedAccountProcessResult;
            }
            nominatedAccount = { ...MOCK_NOMINATED_ACCOUNT, institutionId: 'ins-mock-bank' };
            return { status: 'connected' } satisfies NominatedAccountProcessResult;
          }
          /* The write half of the `profile` topic: a patch, answering the profile as it
             now stands, exactly as the BFF does. Contact changes are NOT OTP-gated here
             — the mock has no verification state to bind one to, and pretending it did
             would teach an author a gate that behaves differently in production. */
          case 'profile.update': {
            const current = topics['profile'] as WidgetProfile;
            const patch = payload as Partial<ProfileUpdateRequest>;
            if (Object.keys(patch ?? {}).length === 0) {
              throw new HostError('invalid-payload', 'profile.update requires at least one field to change');
            }
            const next: WidgetProfile = {
              ...current,
              ...(patch.firstName !== undefined ? { firstName: patch.firstName } : {}),
              ...(patch.middleName !== undefined ? { middleName: patch.middleName } : {}),
              ...(patch.lastName !== undefined ? { lastName: patch.lastName } : {}),
              ...(patch.dob !== undefined ? { dob: patch.dob } : {}),
              ...(patch.email !== undefined ? { email: patch.email } : {}),
              ...(patch.phone !== undefined ? { phone: patch.phone } : {}),
              ...(patch.address ? { address: { ...current.address, ...patch.address } } : {}),
              ...(patch.marketingPreferences
                ? {
                    marketingPreferences: {
                      ...current.marketingPreferences,
                      ...patch.marketingPreferences,
                    },
                  }
                : {}),
            };
            topics['profile'] = next;
            return next;
          }
          case 'lookups.list': {
            const type = String(payload['type'] ?? '') as WidgetLookupType;
            const list = MOCK_LOOKUPS[type];
            if (!list) throw new HostError('invalid-payload', `lookups.list: unknown type '${type}'`);
            return list;
          }
          case 'exchange.rates': {
            const base = payload['base'];
            const symbols = payload['symbols'];
            if (typeof base !== 'string' || !/^[A-Z]{3}$/.test(base) || !Array.isArray(symbols) || !symbols.every((symbol) => typeof symbol === 'string' && /^[A-Z]{3}$/.test(symbol))) {
              throw new HostError('invalid-payload', 'Provide currency codes for base and symbols.');
            }
            return { base, rates: Object.fromEntries(symbols.map((symbol) => [symbol, symbol === base ? 1 : MOCK_EXCHANGE_RATE])) };
          }
          case 'exchange.refresh':
          case 'exchange.quote': {
            const accounts = topics['accounts'] as WidgetAccount[];
            const buy = accounts.find((account) => account.accountId === payload['buyAccountId']);
            const sell = accounts.find((account) => account.accountId === payload['sellAccountId']);
            if (!buy || !sell) throw new HostError('host-error', 'Exchange account not found.');
            if (typeof payload['amount'] !== 'number' || !Number.isFinite(payload['amount']) || payload['amount'] <= 0 || !['buy', 'sell'].includes(String(payload['fixedSide']))) {
              throw new HostError('invalid-payload', 'Provide a positive amount and a fixed side.');
            }
            if (buy === sell && (typeof payload['buyCurrency'] !== 'string' || typeof payload['sellCurrency'] !== 'string' || payload['buyCurrency'] === payload['sellCurrency'])) throw new HostError('invalid-payload', 'Same-wallet exchange requires distinct buy and sell currencies.');
            if ((buy.currencies && !buy.currencies.includes(String(payload['buyCurrency'] ?? buy.currency))) ||
                (sell.currencies && !sell.currencies.includes(String(payload['sellCurrency'] ?? sell.currency)))) throw new HostError('invalid-payload', 'Exchange currency is not held by the account.');
            if (operation === 'exchange.refresh' && !quotes.has(String(payload['quoteId']))) throw new HostError('host-error', 'Quote not found.');
            const quote = { ...mockExchangeQuote({ buyCurrency: buy.currency, sellCurrency: sell.currency, ...payload }, `quote-${++quoteCounter}`), buyAccountId: buy.accountId, sellAccountId: sell.accountId };
            if (operation === 'exchange.refresh') quotes.delete(String(payload['quoteId']));
            quotes.set(quote.quoteId, quote);
            return quote;
          }
          case 'exchange.accept': {
            const quoteId = String(payload['quoteId'] ?? '');
            const quote = quotes.get(quoteId);
            if (!quote) throw new HostError('host-error', `unknown-quote: ${quoteId}`);
            if (Date.parse(quote.expiresAt) <= Date.now()) {
              throw new HostError('host-error', `quote-expired: ${quoteId}`);
            }
            const accounts = topics['accounts'] as WidgetAccount[];
            const buy = accounts.find((account) => account.accountId === quote.buyAccountId);
            const sell = accounts.find((account) => account.accountId === quote.sellAccountId);
            if (!buy || !sell) throw new HostError('host-error', 'Exchange account not found.');
            const sellBalance = sell.balances?.find((balance) => balance.currency === quote.sellCurrency);
            const buyBalance = buy.balances?.find((balance) => balance.currency === quote.buyCurrency);
            const debit = quote.debitAmount ?? quote.sellAmount;
            const credit = quote.creditAmount ?? quote.buyAmount;
            if ((sellBalance?.availableBalance ?? sell.availableBalance) < debit) throw new HostError('host-error', 'Insufficient exchange balance.');
            if (sellBalance) {
              sellBalance.availableBalance = Number(((sellBalance.availableBalance ?? sellBalance.balance ?? 0) - debit).toFixed(2));
              if (sellBalance.balance !== undefined) sellBalance.balance = Number((sellBalance.balance - debit).toFixed(2));
            } else sell.availableBalance = Number((sell.availableBalance - debit).toFixed(2));
            if (buyBalance) {
              buyBalance.availableBalance = Number(((buyBalance.availableBalance ?? buyBalance.balance ?? 0) + credit).toFixed(2));
              if (buyBalance.balance !== undefined) buyBalance.balance = Number((buyBalance.balance + credit).toFixed(2));
            } else buy.availableBalance = Number((buy.availableBalance + credit).toFixed(2));
            for (const account of [buy, sell]) {
              const primary = account.balances?.find((balance) => balance.currency === account.currency);
              if (primary) account.availableBalance = primary.availableBalance ?? primary.balance ?? 0;
            }
            quotes.delete(quoteId);
            const exchange: ExchangeTransactionDetail = {
              buyAccountId: quote.buyAccountId,
              sellAccountId: quote.sellAccountId,
              fixedSide: quote.fixedSide,
              exchangeId: `exch-${quoteId.replace('quote-', '')}`,
              quoteId,
              transactionNumber: `FX-${quoteId.replace('quote-', '')}`,
              status: 'completed',
              buyCurrency: quote.buyCurrency,
              buyAmount: quote.buyAmount,
              sellCurrency: quote.sellCurrency,
              sellAmount: quote.sellAmount,
              exchangeRate: quote.exchangeRate,
              feeAmount: quote.feeAmount,
              feeCurrency: quote.feeCurrency,
              exchangeDate: quote.exchangeDate,
              settlementDate: quote.settlementDate,
              debitAmount: quote.debitAmount,
              creditAmount: quote.creditAmount,
            };
            exchanges.push(exchange);
            return { ...exchange };
          }
          case 'exchange.transactions.get': {
            const exchange = exchanges.find((item) => item.exchangeId === payload['exchangeId']);
            if (!exchange) throw new HostError('host-error', 'Exchange not found.');
            return { ...exchange };
          }
          case 'exchange.transactions.list': {
            if (!(topics['accounts'] as WidgetAccount[]).some((account) => account.accountId === payload['accountId'])) throw new HostError('host-error', 'Account not found.');
            const page = Math.max(1, Number(payload['page'] ?? 1));
            const size = Math.max(1, Number(payload['size'] ?? 20));
            const expandExchangeToAccountSideSummaries = (item: ExchangeTransactionDetail): ExchangeTransactionSummary[] => {
              const sides = [
                ...(item.buyAccountId === payload['accountId'] ? [true] : []),
                ...(item.sellAccountId === payload['accountId'] ? [false] : []),
              ];
              return sides.map((incoming): ExchangeTransactionSummary => ({
                ...item,
                description: `${item.sellCurrency} to ${item.buyCurrency}`,
                transactionNumber: item.transactionNumber!,
                transactionDate: item.exchangeDate!,
                status: item.status!,
                transactionType: incoming ? 'incoming' : 'outgoing',
                currency: incoming ? item.buyCurrency : item.sellCurrency,
                amount: incoming ? item.buyAmount : item.sellAmount,
              }));
            };
            const rows = exchanges.flatMap(expandExchangeToAccountSideSummaries).filter((item) => (!payload['type'] || item.transactionType === payload['type']) &&
              (!payload['currency'] || item.currency === payload['currency']) &&
              (!Array.isArray(payload['status']) || payload['status'].length === 0 || payload['status'].includes(item.status)) &&
              (!payload['from'] || item.transactionDate >= String(payload['from'])) &&
              (!payload['to'] || item.transactionDate <= String(payload['to'])) &&
              `${item.description} ${item.transactionNumber}`.toLowerCase().includes(String(payload['query'] ?? '').toLowerCase()),
            ).reverse();
            return { items: rows.slice((page - 1) * size, page * size), page, totalItems: rows.length };
          }
          case 'paymentRequests.create': {
            // Mirrors the hosts: POST /payments/request rejects an empty
            // paymentReference or transfer reason, and the reason label is
            // resolved from the same catalogue payments.reasons.list serves —
            // so a missing reference/reason, or an id outside the catalogue,
            // fails here exactly as it would in production.
            if (typeof payload['beneficiaryId'] !== 'string' || !payload['beneficiaryId'].trim() || typeof payload['amount'] !== 'number' || !Number.isFinite(payload['amount']) || payload['amount'] <= 0 || typeof payload['currency'] !== 'string' || !/^[A-Z]{3}$/.test(payload['currency'])) {
              throw new HostError('host-error', 'paymentRequests.create requires beneficiaryId, a positive amount and a three-letter currency');
            }
            if (typeof payload['reference'] !== 'string' || !payload['reference'].trim()) {
              throw new HostError(
                'host-error',
                'paymentRequests.create requires reference (POST /payments/request rejects an empty paymentReference)',
              );
            }
            const requestReasonId = payload['transferReasonId'];
            if (!requestReasonId) {
              throw new HostError(
                'host-error',
                'paymentRequests.create requires transferReasonId — pick one via payments.reasons.list',
              );
            }
            if (!MOCK_TRANSFER_REASONS.some((reason) => reason.id === requestReasonId)) {
              throw new HostError(
                'host-error',
                `paymentRequests.create: transferReasonId '${String(
                  requestReasonId,
                )}' is not in the transfer-reason catalogue (payments.reasons.list)`,
              );
            }
            const created: WidgetPaymentRequest = {
              paymentRequestId: `preq-${paymentRequests.items.length + 1}`,
              approvalRecordId: `00000000-0000-4000-8000-${String(paymentRequests.items.length + 1).padStart(12, '0')}`,
              paymentType: 'single',
              amount: Number(payload['amount'] ?? 0),
              currency: String(payload['currency'] ?? 'GBP'),
              status: 'pending',
              createdDate: new Date().toISOString(),
            };
            created.reason = MOCK_TRANSFER_REASONS.find((reason) => reason.id === requestReasonId)?.description;
            if (payload['reference']) created.reference = String(payload['reference']);
            if (payload['sourceAccountId']) created.accountId = String(payload['sourceAccountId']);
            paymentRequests.items.push(created);
            paymentRequests.totalItems = paymentRequests.items.length;
            return { paymentRequestId: created.paymentRequestId } satisfies PaymentRequestCreateResult;
          }
          case 'cards.create': {
            // Mirrors the hosts: POST /cards demands a cardReferenceType (the
            // project's card-product key) and no host derives one, so the mock
            // rejects its absence the same way.
            if (!payload['cardReferenceType']) {
              throw new HostError(
                'host-error',
                'cards.create requires cardReferenceType (the card-product key from the project cards configuration)',
              );
            }
            const cards = topics['cards'] as WidgetCard[];
            const accountId = String(payload['accountId'] ?? '');
            cards.push({
              cardId: `card-${cards.length + 1}`,
              name: 'New virtual card',
              maskedPan: '**** **** **** 0007',
              status: 'active',
              type: 'virtual',
              currency: 'GBP',
            });
            // Mirrors the host: the create response body is discarded, so only
            // the account comes back and the card must be re-listed.
            return { accountId } satisfies CardCreateResult;
          }
          case 'beneficiaries.createInternational': {
            const intlKey = typeof payload['idempotencyKey'] === 'string' ? `beneficiaries.createInternational:${payload['idempotencyKey']}` : undefined;
            if (intlKey && createsByKey.has(intlKey)) return structuredClone(createsByKey.get(intlKey)) as BeneficiaryCreateResult;
            const beneficiaries = topics['beneficiaries'] as WidgetBeneficiary[];
            const created: WidgetBeneficiary = {
              beneficiaryId: `ben-${beneficiaries.length + 1}`,
              name: String(payload['name'] ?? ''),
            };
            if (payload['iban']) created.iban = String(payload['iban']);
            if (payload['accountNumber']) created.accountNumber = String(payload['accountNumber']);
            if (payload['currency']) created.currency = String(payload['currency']);
            beneficiaries.push(created);
            if (intlKey) createsByKey.set(intlKey, { beneficiaryId: created.beneficiaryId });
            return { beneficiaryId: created.beneficiaryId } satisfies BeneficiaryCreateResult;
          }
          case 'payments.precheckInternational':
            return mockInternationalPayment(
              payload,
              topics['beneficiaries'] as WidgetBeneficiary[],
              topics['accounts'] as WidgetAccount[],
            );
          case 'payments.initiateInternational': {
            const result = mockInternationalPayment(
              payload,
              topics['beneficiaries'] as WidgetBeneficiary[],
              topics['accounts'] as WidgetAccount[],
            );
            return {
              ...result,
              transactionId: `intl-${++internationalPaymentCounter}`,
            } satisfies InternationalPaymentResult;
          }
          case 'connections.connect':
            return { authorizationUrl: 'https://oauth.mock.invalid/authorize', expiresAt: new Date(Date.now() + 600_000).toISOString() };
          case 'connections.status':
            return { connected: false, expiresAt: null };
          case 'connections.disconnect':
            return { connected: false };
          case 'documents.download': {
            // Nothing is fetched and nothing is saved; the point of the mock is
            // that the resolved value carries no bytes and no URL, exactly like
            // the real host.
            if (!payload['kind']) throw new HostError('host-error', 'documents.download requires kind');
            // The two stored kinds still have to FIND something: a widget that asks for a
            // document id nothing listed, or a receipt for a transaction that has none, gets
            // the same refusal offline as it would live.
            if (payload['kind'] === 'stored') {
              const key = documentKey(payload['context'], payload['entityId']);
              const found = (storedDocuments[key] ?? []).some(
                (document) => document.documentId === payload['documentId'],
              );
              if (!found) {
                throw new HostError('host-error', `document-not-found: ${String(payload['documentId'])}`);
              }
            }
            if (payload['kind'] === 'transactionReceipt') {
              const key = `transaction:${String(payload['transactionId'] ?? '')}`;
              const receipt = (storedDocuments[key] ?? []).find(
                (document) => document.category === 'receipts',
              );
              if (!receipt) {
                throw new HostError(
                  'host-error',
                  `document-not-found: no receipt for transaction ${String(payload['transactionId'])}`,
                );
              }
            }
            return { started: true } satisfies WidgetDocumentDownloadResult;
          }
          case 'documents.list':
            return [...(storedDocuments[documentKey(payload['context'], payload['entityId'])] ?? [])];
          case 'documents.upload': {
            const key = documentKey(payload['context'], payload['entityId']);
            const contentBase64 = String(payload['contentBase64'] ?? '');
            if (!contentBase64) throw new HostError('host-error', 'documents.upload requires contentBase64');
            const document: WidgetStoredDocument = {
              documentId: `doc-upload-${++uploadedDocumentCounter}`,
              name: String(payload['fileName'] ?? 'upload.bin'),
            };
            if (payload['category']) {
              document.category = payload['category'] as WidgetStoredDocument['category'];
            }
            if (payload['contentType']) document.contentType = String(payload['contentType']);
            document.createdAt = new Date().toISOString();
            storedDocuments[key] = [...(storedDocuments[key] ?? []), document];
            return { documentId: document.documentId };
          }
          case 'documents.delete': {
            const key = documentKey(payload['context'], payload['entityId']);
            const list = storedDocuments[key] ?? [];
            const remaining = list.filter((document) => document.documentId !== payload['documentId']);
            storedDocuments[key] = remaining;
            // Deleting what is already gone is `false`, not an error — same as the BFF.
            return { deleted: remaining.length !== list.length };
          }
          case 'support.subjects.list': {
            const page = Number(payload['page'] ?? 1);
            const limit = Number(payload['limit'] ?? 20);
            const subjects = [{ id: '00000000-0000-4000-8000-000000000001', name: 'General enquiry', relatesToAccount: false }]
              .filter((subject) => subject.name.toLowerCase().includes(String(payload['search'] ?? '').toLowerCase()));
            return { result: subjects.slice((page - 1) * limit, page * limit), page, limit, totalItems: subjects.length, totalPages: Math.ceil(subjects.length / limit) };
          }
          case 'support.welcome.get': return { enabled: true, text: 'How can we help?' };
          case 'support.threads.create': {
            if (payload['subjectId'] !== '00000000-0000-4000-8000-000000000001' || typeof payload['content'] !== 'string' || !payload['content']) {
              throw new HostError('invalid-payload', 'Choose a support subject and enter a message.');
            }
            const now = new Date().toISOString();
            const thread: SupportThreadDetail = {
              id: `support-thread-${supportThreads.length + 1}`, organisationId: 'mock-org',
              clientId: null, clientName: null, subject: String(payload['subject'] ?? 'New message'), status: 'open',
              createdBy: 'mock-user', createdByName: 'You', accountId: typeof payload['accountId'] === 'string' ? payload['accountId'] : null,
              account: null, assignedAgentId: null, assignedAgentName: null, participants: [], canReply: true,
              createdAt: now, updatedAt: now, closedAt: null, closedBy: null,
            };
            supportThreads.push(thread);
            supportMessages.push({ id: `support-message-${supportMessages.length + 1}`, threadId: thread.id, senderId: 'mock-user', senderName: 'You', isAdmin: false, readBySupport: false, content: payload['content'], createdAt: now });
            return { ...thread };
          }
          case 'support.threads.list': {
            const page = Number(payload['page'] ?? 1);
            const limit = Number(payload['limit'] ?? 20);
            const filters = payload['filter'] as string[] | undefined;
            const threads = supportThreads.filter((thread) =>
              (!payload['status'] || thread.status === payload['status']) &&
              thread.subject.toLowerCase().includes(String(payload['search'] ?? '').toLowerCase()) &&
              (!filters?.length || filters.includes('all') || filters.includes('read')),
            ).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
            return { result: threads.slice((page - 1) * limit, page * limit).map((thread) => {
              const lastMessage = supportMessages.filter((message) => message.threadId === thread.id).at(-1);
              return { ...thread, lastSenderName: lastMessage?.senderName ?? null, lastMessage: lastMessage ?? null, unreadCount: 0 };
            }), page, limit, totalItems: threads.length, totalPages: Math.ceil(threads.length / limit) };
          }
          case 'support.unreadCount': return { unreadCount: 0 };
          case 'support.threads.get':
          case 'support.messages.list':
          case 'support.messages.send':
          case 'support.markRead':
          case 'support.typing': {
            const thread = supportThreads.find((item) => item.id === payload['threadId']);
            if (!thread) throw new HostError('host-error', 'Support thread not found.');
            if (operation === 'support.threads.get') return { ...thread };
            if (operation === 'support.typing') {
              if (typeof payload['isTyping'] !== 'boolean') throw new HostError('invalid-payload', 'isTyping must be boolean.');
              return { sent: true };
            }
            const messages = supportMessages.filter((message) => message.threadId === thread.id);
            if (operation === 'support.markRead') {
              if (!messages.some((message) => message.id === payload['lastReadMessageId'])) throw new HostError('invalid-payload', 'Message not found in thread.');
              return { marked: true };
            }
            if (operation === 'support.messages.send') {
              if (typeof payload['content'] !== 'string' || !payload['content']) throw new HostError('invalid-payload', 'Enter a message.');
              const message: SupportMessage = { id: `support-message-${supportMessages.length + 1}`, threadId: thread.id, senderId: 'mock-user', senderName: 'You', isAdmin: false, readBySupport: false, content: payload['content'], createdAt: new Date().toISOString() };
              supportMessages.push(message);
              thread.updatedAt = message.createdAt;
              return { ...message };
            }
            const end = payload['before'] ? messages.findIndex((message) => message.id === payload['before']) : messages.length;
            if (end < 0) throw new HostError('invalid-payload', 'Message cursor not found.');
            const start = Math.max(0, end - Number(payload['limit'] ?? 50));
            const result = messages.slice(start, end);
            return { result, hasMore: start > 0, nextCursor: start > 0 ? result[0].id : null };
          }
          case 'companion.preferences.get': return { ...companionPreferences };
          case 'companion.preferences.update': {
            const choices: Record<string, readonly string[]> = { tone: ['friendly', 'direct'], detail: ['short', 'balanced', 'detailed'], financialLanguage: ['plain', 'technical'], homeStart: ['chat', 'activity'] };
            for (const [key, value] of Object.entries(payload)) {
              const options = Object.hasOwn(choices, key) ? choices[key] : undefined;
              if (options ? typeof value !== 'string' || !options.includes(value) : !['hideBalances', 'hideAccountDetails'].includes(key) || typeof value !== 'boolean') throw new HostError('invalid-payload', 'Invalid companion preference');
            }
            companionPreferences = { ...companionPreferences, ...payload };
            return { ...companionPreferences };
          }
          case 'companion.actions.list': {
            const page = Number(payload['page'] ?? 1), size = Math.min(Number(payload['size'] ?? 20), 50);
            if (!Number.isInteger(page) || page < 1 || !Number.isInteger(size) || size < 1) throw new HostError('invalid-payload', 'Invalid pagination');
            return { items: structuredClone(companionActionLog.slice((page - 1) * size, page * size)), page, size, hasMore: companionActionLog.length > page * size };
          }
          case 'companion.chat': {
            if (typeof payload['message'] !== 'string' || !payload['message']) throw new HostError('invalid-payload', 'message is required');
            const reply = `You said: ${payload['message']}`;
            return { sessionId: String(payload['sessionId'] ?? 'companion-session-1'), reply, blocks: [{ type: 'text', text: reply }] };
          }
          case 'companion.decide': {
            if (!['accepted', 'declined', 'failed'].includes(String(payload['decision']))) throw new HostError('invalid-payload', 'decision must be accepted, declined or failed');
            const outcome = payload['outcome'];
            if (outcome !== undefined && outcome !== 'refused' && outcome !== 'unknown') throw new HostError('invalid-payload', "outcome must be 'refused' or 'unknown'");
            if (outcome !== undefined && payload['decision'] !== 'failed') throw new HostError('invalid-payload', 'outcome is only meaningful with decision failed');
            const proposalId = String(payload['proposalId'] ?? '');
            const attempt = (proposalAttempts.get(proposalId) ?? 0) + (outcome === 'refused' ? 1 : 0);
            proposalAttempts.set(proposalId, attempt);
            const status = payload['decision'] === 'accepted' ? 'reported-accepted' as const : payload['decision'] === 'declined' ? 'declined' as const : outcome === 'refused' ? 'failed-refused' as const : 'unknown' as const;
            let log = companionActionLog.find(item => item.id === proposalId);
            if (!log) { log = { id: proposalId, kind: 'mock.action', summary: 'Mock proposal decision', createdAt: new Date().toISOString(), status, attempts: [] }; companionActionLog.unshift(log); }
            log.status = status;
            log.attempts.push({ attempt: attempt - (outcome === 'refused' ? 1 : 0), status, createdAt: new Date().toISOString(), ...(payload['decision'] === 'failed' && { outcome: outcome === 'refused' ? 'refused' : 'unknown' }) });
            return { proposalId, status: payload['decision'], attempt, blocks: [] };
          }
          case 'companion.memories.list': return { markdown: '', entries: [], settings: { retentionDays: 90 } };
          case 'companion.memories.delete': return { ok: true };
          case 'companion.memories.settings': {
            const retentionDays = Number(payload['retentionDays']);
            if (!Number.isInteger(retentionDays) || retentionDays <= 0) throw new HostError('invalid-payload', 'retentionDays must be a positive integer');
            return { retentionDays };
          }
          case 'companion.sessions.list': {
            const at = new Date(0).toISOString();
            const items = [{ sessionId: 'companion-session-1', title: 'What did I spend last month?', preview: 'You said: What did I spend last month?', messageCount: 2, createdAt: at, updatedAt: at }];
            return { items: Number(payload['page'] ?? 1) === 1 ? items : [], page: Number(payload['page'] ?? 1), size: Math.min(Number(payload['size'] ?? 20), 50), hasMore: false };
          }
          case 'companion.sessions.get': {
            if (payload['sessionId'] !== 'companion-session-1') throw new HostError('host-error', 'Session not found.', undefined, 'not-found');
            const at = new Date(0).toISOString();
            return {
              sessionId: 'companion-session-1',
              messages: [{ role: 'user', text: 'What did I spend last month?', at }, { role: 'assistant', text: 'You said: What did I spend last month?', at }],
              pendingProposals: [],
            };
          }
          case 'companion.sessions.delete':
            if (typeof payload['sessionId'] !== 'string' || !payload['sessionId']) throw new HostError('invalid-payload', 'sessionId is required');
            return { ok: true };
          case 'companion.insights.get': {
            const period = payload['period'];
            if (period !== undefined && !/^\d{4}-(0[1-9]|1[0-2])$/.test(String(period))) throw new HostError('invalid-payload', 'period must be YYYY-MM');
            return { period: { from: '2026-08-01', to: '2026-08-31' }, generatedAt: new Date(0).toISOString(), sections: [] };
          }
          case 'rules.create': {
            const ruleKey = typeof payload['idempotencyKey'] === 'string' ? `rules.create:${payload['idempotencyKey']}` : undefined;
            if (ruleKey && createsByKey.has(ruleKey)) return structuredClone(createsByKey.get(ruleKey));
            const action = (payload['action'] ?? {}) as { transferPercent?: number; transferAmount?: number; toAccountId?: string };
            if (payload['trigger'] !== 'inboundCredit' || !action.toAccountId || (action.transferPercent === undefined) === (action.transferAmount === undefined)) {
              throw new HostError('invalid-payload', "trigger 'inboundCredit', action.toAccountId and exactly one of transferPercent/transferAmount are required");
            }
            const rule: import('./contracts').CompanionRule = {
              id: `rule-${companionRules.length + 1}`, organisationId: 'org-1', createdByUserId: 'user-1', trigger: 'inboundCredit',
              match: (payload['match'] ?? {}) as import('./contracts').CompanionRule['match'], action: { ...action, toAccountId: action.toAccountId },
              limits: (payload['limits'] ?? {}) as import('./contracts').CompanionRule['limits'], status: 'active', createdAt: new Date(0).toISOString(),
            };
            companionRules.push(rule);
            if (ruleKey) createsByKey.set(ruleKey, structuredClone(rule));
            return structuredClone(rule);
          }
          case 'rules.list': return structuredClone(companionRules);
          case 'rules.cancel': {
            const rule = companionRules.find((r) => r.id === payload['ruleId']);
            if (!rule) throw new HostError('invalid-payload', 'rule not found');
            rule.status = 'cancelled';
            return structuredClone(rule);
          }
          case 'notifications.preferences.get': return structuredClone(notificationPreferences);
          case 'notifications.preferences.update': {
            const patch = payload['preferences'];
            if (!patch || typeof patch !== 'object' || Array.isArray(patch)) throw new HostError('invalid-payload', 'preferences must be an object');
            for (const [category, channels] of Object.entries(patch)) {
              if (!Object.hasOwn(notificationPreferences, category) || !channels || typeof channels !== 'object' || Array.isArray(channels) || Object.entries(channels).some(([key, value]) => !['email', 'sms', 'inApp', 'push'].includes(key) || typeof value !== 'boolean')) throw new HostError('invalid-payload', 'Unknown category or invalid channel preference');
            }
            for (const [category, channels] of Object.entries(patch)) notificationPreferences[category] = { ...notificationPreferences[category], ...channels as object };
            return structuredClone(notificationPreferences);
          }
          case 'notifications.list': {
            const feed = topics['notifications'] as WidgetNotification[];
            const page = Number(payload['page'] ?? 1);
            const size = Number(payload['size'] ?? 10);
            const start = (page - 1) * size;
            const read = payload['read'];
            if (read !== undefined && typeof read !== 'boolean') {
              throw new HostError('invalid-payload', 'notifications.list read must be boolean');
            }
            const matching =
              read === undefined ? feed : feed.filter((notification) => notification.read === read);
            return {
              items: matching.slice(start, start + size).map((notification) => ({ ...notification })),
              page,
              totalItems: matching.length,
              unreadCount: feed.filter((notification) => !notification.read).length,
            };
          }
          case 'notifications.markRead': {
            const feed = topics['notifications'] as WidgetNotification[];
            const ids = Array.isArray(payload['ids']) ? (payload['ids'] as string[]) : undefined;
            const marked = feed.filter((notification) => !ids || ids.includes(notification.id));
            for (const notification of marked) notification.read = true;
            return { marked: ids ? ids.length : 0 };
          }
          case 'notifications.registerDevice': {
            pushTokens.add(String(payload['token'] ?? ''));
            return { registered: true } satisfies WidgetPushRegisterResult;
          }
          case 'notifications.unregisterDevice': {
            // Dropping a token the subscriber never held is a quiet success, as
            // it is on the BFF — an application that logs out twice is not wrong.
            pushTokens.delete(String(payload['token'] ?? ''));
            return { unregistered: true } satisfies WidgetPushUnregisterResult;
          }
          case 'cards.wallet.beginProvisioning': {
            const cardId = String(payload['cardId'] ?? '');
            const card = (topics['cards'] as WidgetCard[]).find((row) => row.cardId === cardId);
            if (!card) {
              throw new HostError('host-error', `cards.wallet.beginProvisioning: no card ${cardId}`);
            }
            const lastFour = (card.maskedPan ?? '0000').slice(-4);
            if (payload['wallet'] === 'google') {
              // The OPC is opaque by definition, so the mock's is a placeholder of
              // the right SHAPE (base64) and nothing more: TapAndPay rejects it,
              // which is the honest offline answer.
              return {
                wallet: 'google',
                opaquePaymentCard: base64('mock-opaque-payment-card-' + cardId),
                cardNetwork: 'MASTERCARD',
                tokenProvider: 'MASTERCARD',
                displayName: card.name ?? 'Card',
                lastDigits: lastFour,
              } satisfies WidgetWalletProvisioningPayload;
            }
            return {
              wallet: 'apple',
              encryptionScheme: 'ECC_V2',
              cardholderName: 'Alex Customer',
              localizedDescription: card.name ?? 'Card',
              primaryAccountSuffix: lastFour,
              paymentNetwork: 'MasterCard',
            } satisfies WidgetWalletProvisioningPayload;
          }
          case 'cards.wallet.completeProvisioning': {
            const cardId = String(payload['cardId'] ?? '');
            if (!Array.isArray(payload['certificates']) || payload['certificates'].length === 0) {
              throw new HostError(
                'host-error',
                'cards.wallet.completeProvisioning requires the certificates PKAddPaymentPassViewController hands its delegate',
              );
            }
            return {
              encryptedData: base64('mock-encrypted-pass-' + cardId),
              ephemeralPublicKey: base64('mock-ephemeral-public-key'),
              activationData: base64('mock-activation-data'),
            } satisfies WidgetWalletCompleteProvisioningResult;
          }
          case 'profile.changePassword': {
            if (!payload['currentPassword'] || !payload['newPassword']) {
              throw new HostError(
                'host-error',
                'profile.changePassword requires currentPassword and newPassword',
              );
            }
            // One seeded wrong password, so the refusal branch is reachable offline without
            // the mock having to hold a credential.
            if (payload['currentPassword'] === 'wrong') {
              // 'host-error' rather than the BFF's 'forbidden': that code travels on the HTTP
              // envelope, and the postMessage protocol's WidgetErrorCode has no member for it.
              // The slug still reaches the app, as serverCode, the way the BFF's does.
              throw new HostError(
                'host-error',
                'forbidden: profile.changePassword — the current password is not correct',
                undefined,
                'forbidden',
              );
            }
            return { changed: true as const };
          }
          // Offline fixture workflow, not a CSV parser or a payment/provider simulator.
          case 'bulkPayments.create': {
            if (
              typeof payload['fileName'] !== 'string' ||
              !payload['fileName'].endsWith('.csv') ||
              typeof payload['contentBase64'] !== 'string' ||
              !payload['contentBase64'] ||
              typeof payload['accountId'] !== 'string' ||
              !payload['accountId'] ||
              typeof payload['showInBeneficiaryList'] !== 'boolean'
            ) {
              throw new HostError(
                'invalid-payload',
                'CSV content, filename, account and beneficiary preference are required',
              );
            }
            const bulkPaymentId = `bulk-upload-${++bulkPaymentCounter}`;
            const rows = MOCK_BULK_PAYMENT_TRANSFERS['bulk-1'].map((row, index) => ({
              ...row,
              transferId: `${bulkPaymentId}-row-${index + 1}`,
            }));
            bulkPaymentTransfers[bulkPaymentId] = rows;
            bulkPayments.items.unshift({
              bulkPaymentId,
              fileName: payload['fileName'],
              accountId: payload['accountId'],
              status: 'pending',
              totalPayments: rows.length,
              totalAmount: rows.reduce((sum, row) => sum + row.amount, 0),
              currency: 'GBP',
            });
            bulkPayments.totalItems = bulkPayments.items.length;
            return { bulkPaymentId };
          }
          case 'bulkPayments.addBeneficiaries':
          case 'bulkPayments.removeTransfer':
          case 'bulkPayments.updatePayee':
          case 'bulkPayments.requestApproval':
          case 'bulkPayments.execute':
          case 'bulkPayments.cancel': {
            const bulkPaymentId = String(payload['bulkPaymentId'] ?? '');
            const batch = bulkPayments.items.find((item) => item.bulkPaymentId === bulkPaymentId);
            const rows = bulkPaymentTransfers[bulkPaymentId];
            if (!batch || !rows) throw new HostError('host-error', `unknown-bulk-payment: ${bulkPaymentId}`);
            if (operation === 'bulkPayments.removeTransfer' || operation === 'bulkPayments.updatePayee') {
              const transferId = String(payload['transferId'] ?? '');
              const row = rows.find((item) => item.transferId === transferId);
              if (!row) throw new HostError('host-error', `unknown-bulk-transfer: ${transferId}`);
              if (operation === 'bulkPayments.removeTransfer') {
                bulkPaymentTransfers[bulkPaymentId] = rows.filter((item) => item !== row);
                batch.totalPayments = rows.length - 1;
                batch.totalAmount -= row.amount;
              } else {
                if (typeof payload['suggestion'] !== 'boolean')
                  throw new HostError('invalid-payload', 'suggestion must be boolean');
                row.beneficiaryCoPName = payload['suggestion']
                  ? row.beneficiaryCoPName || String(payload['name'] ?? '')
                  : row.beneficiaryName;
              }
              return { bulkPaymentId, transferId };
            }
            if (operation === 'bulkPayments.addBeneficiaries') {
              if (typeof payload['showInBeneficiaryList'] !== 'boolean')
                throw new HostError('invalid-payload', 'showInBeneficiaryList must be boolean');
            } else {
              batch.status =
                operation === 'bulkPayments.requestApproval'
                  ? 'pending approval'
                  : operation === 'bulkPayments.cancel'
                    ? 'cancelled'
                    : 'authenticated';
            }
            return { bulkPaymentId };
          }
          case 'bulkPayments.list':
            return bulkPayments;
          case 'bulkPayments.get': {
            const bulkPaymentId = String(payload['bulkPaymentId'] ?? '');
            const transfers = bulkPaymentTransfers[bulkPaymentId];
            if (!transfers) throw new HostError('host-error', `unknown-bulk-payment: ${bulkPaymentId}`);
            const batch = bulkPayments.items.find((item) => item.bulkPaymentId === bulkPaymentId);
            const detail: WidgetBulkPaymentDetail = {
              bulkPaymentId,
              items: transfers,
              page: 1,
              totalItems: transfers.length,
            };
            if (batch) {
              detail.summary = {
                status: batch.status,
                totalAmount: batch.totalAmount,
                currency: batch.currency,
                accountId: batch.accountId,
                accountName: batch.accountName,
                totalPayments: batch.totalPayments,
                totalSuccessPayments: batch.totalSuccessPayments,
                totalFailedPayments: batch.totalFailedPayments,
                executedDate: batch.executedDate,
              };
            }
            return detail;
          }
          case 'bulkPayments.retry': {
            const bulkPaymentId = String(payload['bulkPaymentId'] ?? '');
            const transferId = String(payload['transferId'] ?? '');
            const transfer = bulkPaymentTransfers[bulkPaymentId]?.find(
              (candidate) => candidate.transferId === transferId,
            );
            if (!transfer) throw new HostError('host-error', `unknown-bulk-transfer: ${transferId}`);
            // A retry the host accepted puts the row back in flight; the widget
            // learns the outcome by re-reading bulkPayments.get.
            transfer.status = 'pending';
            transfer.transactionStatus = 'pending';
            delete transfer.isError;
            return { bulkPaymentId, transferId };
          }
          case 'payments.schemes.list':
            return MOCK_PAYMENT_SCHEMES;
          case 'onboarding.flow': {
            const kind = payload['kind'] === 'business' ? 'business' : 'individual';
            return buildOnboardingFlow({
              kind,
              email: payload['email'] === undefined ? undefined : String(payload['email']),
              phone: payload['phone'] === undefined ? undefined : String(payload['phone']),
              verified: verifiedContacts,
              submitted: onboardingSubmitted,
              identityBeforeSubmit: kind === 'individual' && overrides.identity?.provider === 'veriff' && !!widgetAuthStatus(authIdentity).authenticated,
              identityStatus: kind === 'business' ? (onboardingKyb ?? undefined) : onboardingKyc,
              requirements: { ...onboardingRequirements, kind },
            });
          }
          case 'onboarding.progress.save': {
            const id = String(payload['formSessionId'] ?? `00000000-0000-4000-8000-${(++onboardingDraftSequence).toString(16).padStart(12, '0')}`);
            if (payload['formSessionId'] && !onboardingDrafts.has(id)) throw new HostError('invalid-payload', 'Draft unavailable');
            const draft = { formSessionId: id, source: payload['source'] as 'web' | 'mobile', step: String(payload['step']), data: payload['data'] as Record<string, unknown>, expiresAt: new Date(Date.now() + 86400000).toISOString() };
            onboardingDrafts.set(id, draft);
            return { formSessionId: id, expiresAt: draft.expiresAt };
          }
          case 'onboarding.progress.load': {
            const draft = onboardingDrafts.get(String(payload['formSessionId']));
            if (!draft) throw new HostError('invalid-payload', 'Draft unavailable');
            return draft;
          }
          case 'onboarding.progress.complete':
            onboardingDrafts.delete(String(payload['formSessionId']));
            return { completed: true };
          case 'onboarding.requirements': {
            const kind = payload['kind'] === 'business' ? 'business' : 'individual';
            return {
              ...onboardingRequirements,
              kind,
              // The typed required fields are per kind and are not a project
              // choice, so they are recomputed rather than taken from the seed.
              requiredFields: onboardingRequiredFields(kind),
            } satisfies WidgetOnboardingRequirements;
          }
          case 'onboarding.bank.verify': {
            if (!/^\d{6}$/.test(String(payload['sortCode'] ?? '').replace(/[- ]/g, '')) || !/^\d{8}$/.test(String(payload['accountNumber'] ?? '')) || !payload['name'])
              throw new HostError('invalid-payload', 'Enter valid nominated account details.');
            return { name: String(payload['name']), matchStatus: payload['sortCode'] !== '000000', matchStatusCode: payload['sortCode'] === '000000' ? 'NO_MATCH' : 'MATCH', expiresAt: new Date(Date.now() + 30 * 60_000).toISOString() };
          }
          case 'onboarding.verify.send': {
            const channel = payload['channel'] === 'phone' ? 'phone' : 'email';
            const value = String(payload['value'] ?? '');
            if (!value) throw new HostError('host-error', 'onboarding.verify.send requires value');
            assertMockOtpRateLimit('onboarding.verify.send', channel);
            // Nothing is sent, and the code is never returned: MOCK_OTP_CODES is
            // the contract with the *test*, not with the widget, so a widget
            // cannot learn a code from a send result here or in production.
            return {
              channel,
              codeLength: OTP_CODE_LENGTH[channel],
              resendAfterSeconds: OTP_RESEND_AFTER_SECONDS,
            } satisfies WidgetVerificationSendResult;
          }
          case 'onboarding.verify.confirm': {
            const channel = payload['channel'] === 'phone' ? 'phone' : 'email';
            const value = String(payload['value'] ?? '');
            if (!value) throw new HostError('host-error', 'onboarding.verify.confirm requires value');
            assertMockOtpRateLimit('onboarding.verify.confirm', channel);
            if (String(payload['code'] ?? '') !== MOCK_OTP_CODES[channel]) {
              // The account-service's slug, so the mock HTTP transport answers 400
              // `verification-rejected` exactly as live and the app client maps it
              // to `confirmation-rejected`.
              throw new HostError('host-error', OTP_CODE_REJECTED_MESSAGE, undefined, 'verification-rejected');
            }
            const expiresAt = verificationExpiresAt(new Date());
            verifiedContacts[channel] = { value, expiresAt };
            // Minted and kept host-side, exactly as the real host does — and
            // deliberately absent from the result the widget receives.
            verificationTokens[channel] = `mock-${channel}-verification-token`;
            publishOnboardingStatus();
            return { channel, verified: true, expiresAt } satisfies WidgetVerificationConfirmResult;
          }
          case 'onboarding.submitIndividual': {
            const request = payload as unknown as Partial<OnboardIndividualRequest>;
            if (!request.firstName || !request.lastName || !request.dob) {
              throw new HostError(
                'host-error',
                'onboarding.submitIndividual requires firstName, lastName and dob',
              );
            }
            if (!request.email || !request.phone) {
              throw new HostError('host-error', 'onboarding.submitIndividual requires email and phone');
            }
            // The gate runs before anything else, exactly as the bridge runs it
            // before any HTTP call.
            assertMockPrerequisites('onboarding.submitIndividual', request.email, request.phone);
            // The real submit carries the host-held single-use tokens; a missing
            // one is the same failure as an unverified channel, so the mock
            // reports it the same way rather than resolving on a submit the real
            // server would answer with a 403.
            const untokenised: WidgetOnboardingStepId[] = [];
            if (!verificationTokens.email) untokenised.push('verify.email');
            if (!verificationTokens.phone) untokenised.push('verify.phone');
            if (untokenised.length) {
              throw new HostError(
                'onboarding-prerequisite-unmet',
                prerequisiteUnmetMessage('onboarding.submitIndividual', untokenised),
                { outstanding: untokenised },
              );
            }
            onboardingKind = 'individual';
            onboardingSubmitted = true;
            if (onboardingKyc !== 'success') onboardingKyc = 'pending';
            onboardingProfileComplete = true;
            // Consumed on a successful submit, as the server consumes them.
            delete verificationTokens.email;
            delete verificationTokens.phone;
            publishOnboardingStatus();
            const userId = MOCK_WIDGET_CONTEXT.user.id;
            const followUp = applyMockSubmitFollowUp(normaliseSubmitFollowUp(request.onSuccess), {
              userId,
              activeRole: MOCK_WIDGET_CONTEXT.user.roles[0],
            });
            return { status: 'pending', userId, ...followUp } satisfies OnboardIndividualResult;
          }
          case 'onboarding.submitBusiness': {
            const request = payload as unknown as Partial<OnboardBusinessRequest>;
            const invalid = validateMockBusinessOnboarding(request);
            if (invalid) throw new HostError('host-error', invalid);
            // Rejected before anything happens, with the shared message, because
            // the real endpoint returns no session either — a widget must not
            // learn that business 'signIn' works.
            const business = normaliseBusinessFollowUp(request.onSuccess);
            if (business.unsupported) {
              throw new HostError('host-error', BUSINESS_SIGN_IN_UNSUPPORTED_MESSAGE);
            }
            const primary = primaryBusinessMember(request);
            // The server runs the same gate on the primary member's contacts
            // and requires the host-held single-use tokens on the submit —
            // mirror both, exactly like the individual case above.
            assertMockPrerequisites('onboarding.submitBusiness', primary?.email, primary?.phone);
            const businessUntokenised: WidgetOnboardingStepId[] = [];
            if (!verificationTokens.email) businessUntokenised.push('verify.email');
            if (!verificationTokens.phone) businessUntokenised.push('verify.phone');
            if (businessUntokenised.length) {
              throw new HostError(
                'onboarding-prerequisite-unmet',
                prerequisiteUnmetMessage('onboarding.submitBusiness', businessUntokenised),
                { outstanding: businessUntokenised },
              );
            }
            onboardingKind = 'business';
            onboardingSubmitted = true;
            onboardingKyb = 'pending';
            // Consumed on a successful submit, as the server consumes them.
            delete verificationTokens.email;
            delete verificationTokens.phone;
            publishOnboardingStatus();
            // No session to consume, so no sign-in is possible here — only the
            // redirect flag can be true.
            const { redirected } = applyMockSubmitFollowUp(business.followUp, {});
            return { ...MOCK_ONBOARDING_BUSINESS_RESULT, redirected } satisfies OnboardBusinessResult;
          }
          case 'auth.passwordPolicy':
            return { policy: 'length(8) and digits(1)' };
          case 'auth.status':
            return widgetAuthStatus(authIdentity);
          case 'auth.login': {
            // Nothing to navigate to (see applyMockSubmitFollowUp), so the
            // declaration is the record and the widget stays alive.
            const detail: Record<string, unknown> = { action: 'login' };
            if (payload['redirectPageId'] !== undefined) {
              detail['redirectPageId'] = String(payload['redirectPageId']);
            }
            records.push({ kind: 'auth', detail });
            return { navigated: true };
          }
          case 'auth.logout':
            // The real host revokes server-side and redirects; the mock does the
            // one part that is observable to a widget — the session is gone, so a
            // following auth.status answers `{ authenticated: false }`.
            authIdentity = {};
            records.push({ kind: 'auth', detail: { action: 'logout' } });
            return { signedOut: true };
          case 'auth.flow.start': {
            const loginHint = payload['loginHint'];
            if (loginHint !== undefined && typeof loginHint !== 'string') {
              throw new HostError('host-error', 'auth.flow.start: loginHint must be a string');
            }
            // A start always discards whatever was in progress — there is no
            // resuming a login transaction, in the mock or in Keycloak.
            authFlowIndex = null;
            return advanceMockAuthFlow(0);
          }
          case 'auth.flow.submit': {
            const { index, seed } = currentAuthFlow('auth.flow.submit');
            const action = payload['action'];
            if (action !== undefined && typeof action !== 'string') {
              throw new HostError('host-error', 'auth.flow.submit: action must be a string');
            }
            if (action) {
              if (!(seed.actions ?? []).includes(action)) {
                throw new HostError(
                  'host-error',
                  authFlowActionUnavailableMessage('auth.flow.submit', action),
                );
              }
              if (action === 'restart_flow') {
                authFlowIndex = null;
                return advanceMockAuthFlow(0);
              }
              // 'resend_code' re-arms the challenge and comes back with the same
              // step, which is also the honest answer for a recovery branch the
              // mock has no script for.
              return authFlowStepAt(index);
            }
            const rawValues = payload['values'];
            if (!rawValues || typeof rawValues !== 'object' || Array.isArray(rawValues)) {
              throw new HostError(
                'host-error',
                'auth.flow.submit requires values: an object keyed by the step’s field names',
              );
            }
            const values = rawValues as Record<string, unknown>;
            for (const field of seed.fields ?? []) {
              if (!field.required) continue;
              const value = values[field.name];
              const filled = Array.isArray(value) ? value.length > 0 : String(value ?? '').trim().length > 0;
              // Two different failures, two different shapes: a missing required
              // field is a widget bug (the step said it was required) and throws,
              // while a *wrong* value is an ordinary login outcome and comes back
              // as the same step carrying `error`.
              if (!filled) {
                throw new HostError('host-error', `auth.flow.submit requires ${field.name}`);
              }
            }
            const rejected = Object.entries(seed.reject ?? {}).some(
              ([name, value]) => String(values[name] ?? '') === value,
            );
            const unmet = Object.entries(seed.expect ?? {}).some(
              ([name, value]) => String(values[name] ?? '') !== value,
            );
            if (rejected || unmet) {
              return authFlowStepAt(
                index,
                seed.error ?? { code: 'invalid_credentials', message: 'That submission was rejected' },
              );
            }
            return advanceMockAuthFlow(index + 1);
          }
          case 'auth.flow.selectMethod': {
            const { index, seed } = currentAuthFlow('auth.flow.selectMethod');
            const methodId = String(payload['methodId'] ?? '');
            const chosen = (seed.methods ?? []).find((method) => method.id === methodId);
            // The server validates the execution id against the current
            // selection set and rejects anything else; an id from an earlier
            // step, or one a widget assembled, is not honoured here either.
            if (!chosen) {
              throw new HostError(
                'host-error',
                `auth.flow.selectMethod: '${methodId}' is not one of the current step's methods. ` +
                  'Pass a WidgetAuthFlowMethod.id back unchanged.',
              );
            }
            return advanceMockAuthFlow(chosen.goTo ?? index + 1);
          }
          case 'auth.flow.cancel': {
            const cancelled = authFlowIndex !== null;
            authFlowIndex = null;
            if (cancelled) records.push({ kind: 'auth', detail: { action: 'flow.cancel' } });
            return { cancelled };
          }
          case 'providerCapabilities.list':
            return { provider: 'mock', capabilities: [] };
          case 'providerCapabilities.invoke':
            throw new HostError(
              'unknown-operation',
              `providerCapabilities.invoke: mock host has no capability '${String((payload as { capability?: string })?.capability ?? '')}'`,
            );
          case 'onboarding.verifyIdentity': {
            if (!onboardingSubmitted) {
              throw new HostError(
                'onboarding-prerequisite-unmet',
                prerequisiteUnmetMessage('onboarding.verifyIdentity', ['submit']),
                { outstanding: ['submit'] },
              );
            }
            const level = payload['level'] === 'business' ? 'business' : onboardingKind;
            // No vendor to launch and — the point of the mock — no provider
            // token in the resolved value, exactly like the real host.
            if (level === 'business') onboardingKyb = 'in-progress';
            else onboardingKyc = 'in-progress';
            publishOnboardingStatus();
            return { launched: true } satisfies WidgetVerifyIdentityResult;
          }
          case 'identity.verification.profile':
            return {
              provider: 'veriff', status: onboardingKyc,
              ...(onboardingKyc === 'success' ? { person: overrides.identity?.person, document: overrides.identity?.document } : {}),
            } satisfies IdentityVerificationProfile;
          case 'identity.verification.start': {
            if (onboardingKyc === 'success') {
              return { status: 'already-verified' } satisfies IdentityVerificationStartResult;
            }
            if (!onboardingSubmitted && overrides.identity?.provider === 'sumsub') {
              throw new HostError('onboarding-prerequisite-unmet', prerequisiteUnmetMessage('identity.verification.start', ['submit']), { outstanding: ['submit'] });
            }
            // Starting a session decides nothing, here as on the platform: the status moves
            // only when the decision arrives (setKycStatus stands in for the webhook).
            const provider = overrides.identity?.provider ?? 'veriff';
            if (provider === 'veriff') {
              return { status: 'launch', provider, sessionUrl: MOCK_VERIFF_SESSION_URL } satisfies IdentityVerificationStartResult;
            }
            if (provider === 'sumsub') {
              return { status: 'launch', provider, accessToken: MOCK_SUMSUB_ACCESS_TOKEN } satisfies IdentityVerificationStartResult;
            }
            return { status: 'unsupported', provider } satisfies IdentityVerificationStartResult;
          }
          /* Data Studio records: the operation form of the storage handlers above,
             over the very same seeds — a code application and a widget therefore
             see one set of documents in a scenario, not two. */
          case 'records.list':
            return dataDefinitions.map((definition) => ({ ...definition }));
          case 'records.get': {
            const reference = recordReference(payload);
            collections.assertDocument(reference);
            return (await readRecord(reference)) ?? null;
          }
          case 'records.create': {
            const reference = recordReference(payload);
            collections.assertDocument(reference);
            return writeRecord(
              reference,
              payload['value'] as Record<string, unknown>,
              // Revision 0 is the create-only guard the BFF passes: nothing may be
              // here yet, or this is a `conflict`.
              { ifRevision: 0 },
            );
          }
          case 'records.update': {
            const reference = recordReference(payload);
            collections.assertDocument(reference);
            return writeRecord(
              reference,
              payload['value'] as Record<string, unknown>,
              payload['ifRevision'] === undefined
                ? undefined
                : { ifRevision: Number(payload['ifRevision']) },
            );
          }
          case 'records.delete': {
            const reference = recordReference(payload);
            collections.assertDocument(reference);
            const existing = await readRecord(reference);
            await deleteRecord(
              reference,
              payload['ifRevision'] === undefined
                ? undefined
                : { ifRevision: Number(payload['ifRevision']) },
            );
            return { definition: reference, deleted: existing !== undefined };
          }
          /* Collections: the same seeds, many rows per definition. The store is
             mock-collections.ts, which is also where a `user`-scoped definition
             confines every read and write to the signed-in subject's own rows. */
          case 'records.items.query':
            return collections.query(recordReference(payload), payload);
          case 'records.items.get':
            return collections.get(recordReference(payload), payload);
          case 'records.items.create':
            return collections.create(recordReference(payload), payload);
          case 'records.items.update':
            return collections.update(recordReference(payload), payload);
          case 'records.items.delete':
            return collections.remove(recordReference(payload), payload);
          case 'cards.freeze':
          case 'cards.unfreeze': {
            const status = operation === 'cards.freeze' ? 'frozen' : 'active';
            const card = (topics['cards'] as WidgetCard[]).find(
              (candidate) => candidate.cardId === payload['cardId'],
            );
            if (card) card.status = status;
            return { status };
          }
          case 'device.enroll': {
            const alias = typeof payload['alias'] === 'string' ? (payload['alias'] as string) : 'This device';
            const deviceId =
              typeof payload['deviceId'] === 'string' && payload['deviceId']
                ? (payload['deviceId'] as string)
                : `mock-device-${enrolledDevices.length + 1}`;
            enrolledDevices.push({
              deviceId,
              alias,
              deviceInfo: typeof payload['deviceInfo'] === 'string' ? (payload['deviceInfo'] as string) : undefined,
              algorithm: typeof payload['algorithm'] === 'string' ? (payload['algorithm'] as string) : 'ES256',
              createdAt: new Date().toISOString(),
              current: true,
            });
            return { deviceId, alias };
          }
          case 'device.list':
            return { devices: enrolledDevices.map((device) => ({ ...device })) };
          case 'device.remove': {
            const index = enrolledDevices.findIndex((device) => device.deviceId === payload['deviceId']);
            if (index < 0) {
              throw new HostError('invalid-payload', `No enrolled device '${String(payload['deviceId'])}'`);
            }
            enrolledDevices.splice(index, 1);
            return { removed: true };
          }
          case 'session.biometric.enroll': {
            if (!authIdentity.userId) throw new HostError('host-error', 'session.biometric.enroll needs a signed-in session');
            biometricSequence += 1;
            // Shaped like a JWT so code that checks the shape is exercised; the mock never reads it.
            const offlineToken = `mock.offline-credential.${biometricSequence}`;
            const credentialId = `mock-credential-${biometricSequence}`;
            biometricCredentials.set(offlineToken, credentialId);
            records.push({ kind: 'auth', detail: { action: 'biometric.enroll', credentialId } });
            return { offlineToken, credentialId } satisfies WidgetApiOperations['session.biometric.enroll']['result'];
          }
          case 'session.biometric.revoke': {
            const credentialId = String(payload['credentialId'] ?? '');
            if (!credentialId) throw new HostError('invalid-payload', 'session.biometric.revoke requires credentialId');
            let revoked = false;
            for (const [token, id] of biometricCredentials) {
              if (id === credentialId) {
                biometricCredentials.delete(token);
                revoked = true;
              }
            }
            records.push({ kind: 'auth', detail: { action: 'biometric.revoke', credentialId, revoked } });
            return { revoked } satisfies WidgetApiOperations['session.biometric.revoke']['result'];
          }
          default:
            return { ok: true, operation, echo: payload };
        }
      },
    },
    forms: {
      get: async (path) => forms[path],
      set: async (path, value) => {
        forms[path] = value;
        records.push({ kind: 'form-set', detail: { path, value } });
      },
      validity: async () => true,
    },
    nav: {
      goToPage: async (pageId) => void records.push({ kind: 'nav', detail: { pageId } }),
      back: async () => void records.push({ kind: 'nav', detail: { back: true } }),
    },
    ui: {
      notify: async (notification) => void records.push({ kind: 'notify', detail: { ...notification } }),
      confirm: async (confirmation) => {
        records.push({ kind: 'confirm', detail: { ...confirmation } });
        return true;
      },
    },
    onEvent: (name, payload) => records.push({ kind: 'event', detail: { name, payload } }),
    ...(overrides.handlers ?? {}),
  };

  const host = createWidgetHost({
    context: resolvedContext,
    grants: resolvedGrants,
    handlers,
  });

  function setKycStatus(status: WidgetOnboardingCheckStatus): void {
    onboardingKyc = status;
    if (status !== 'not-started' && overrides.identity?.provider === 'sumsub') onboardingSubmitted = true;
    const profile = topics['profile'];
    if (profile && typeof profile === 'object') topics['profile'] = { ...profile, kyc: status };
    publishOnboardingStatus();
  }

  function redeemBiometricCredential(offlineToken: string): boolean {
    if (!biometricCredentials.has(offlineToken)) return false;
    authIdentity = {
      userId: overrides.auth?.userId ?? resolvedContext.user?.id ?? MOCK_WIDGET_CONTEXT.user.id,
      activeRole: overrides.auth?.roles?.[0] ?? resolvedContext.user?.roles?.[0],
    };
    records.push({ kind: 'auth', detail: { action: 'biometric.unlock', credentialId: biometricCredentials.get(offlineToken) } });
    return true;
  }

  return Object.assign(host, { records, forms, handlers, setKycStatus, redeemBiometricCredential });
}
